<template>
  <section class="section-container">
    <div class="content-wrapper">
      <div class="content-heading">
        {{ $t('tenderview.tender') }}
      </div>
      <div class="card" v-if="!loader">
        <div class="table-responsive">
          <table class="table">
            <tbody>
              <tr>
                <td class="b0">
                  <p class="h4 mt-0">
                    <span class="align-middle text-bold link-unstyled">{{ $t('tenderview.tender') }} {{ tender.tender_code }}</span>
                    <span class="rounded badge bg-success-light px-4 ml-2 text-sm d-lg-inline" v-if="is_active">
                      <span class="text-white">{{ $t('tenders.state.active') }}</span>
                    </span>
                    <span class="rounded badge bg-warning-light px-4 ml-2 text-sm d-lg-inline" v-if="is_waiting">
                      <span class="text-white">{{ $t('tenders.state.waiting') }}</span>
                    </span>
                    <span class="rounded badge bg-warning-light px-4 ml-2 text-sm d-lg-inline" v-if="is_finished">
                      <span class="text-white">{{ $t('tenders.state.finished') }}</span>
                    </span>
                    <span class="rounded badge bg-warning-dark px-4 ml-2 text-sm d-lg-inline" v-if="is_choosing">
                      <span class="text-white">{{ $t('tenders.state.choosing') }}</span>
                    </span>
                    <span class="rounded badge bg-info-light px-4 ml-2 text-sm d-lg-inline" v-if="is_ordered">
                      <span class="text-white">{{ $t('tenders.state.ordered') }}</span>
                    </span>
                    <span class="rounded badge bg-danger-dark px-4 ml-2 text-sm d-lg-inline" v-if="is_canceled">
                      <span class="text-white">{{ $t('tenders.state.canceled') }}</span>
                    </span>
                    <span class="rounded badge bg-danger-light px-4 ml-2 text-sm d-lg-inline" v-if="is_failed">
                      <span class="text-white">{{ $t('tenders.state.failed') }}</span>
                    </span>
                    <router-link
                      v-if="is_ordered"
                      :to="{name: 'tender_history', params: {id: tender.id}}"
                      target="_blank"
                      class="btn btn-link text-warning"
                      :title="$t('tenderview.show-proposals')">
                      <i class="fas fa-history"></i>
                    </router-link>
                    <span v-if="is_supplier">
                      <button
                          v-if="!tender.watched"
                          class="btn btn-link pl-1 pt-1 text-info"
                          :title="$t('tenderview.add-watched')"
                          @click="add_watched(tender.id)">
                        <i class="far fa-eye text-success"></i>
                      </button>
                      <button
                          v-if="tender.watched"
                          class="btn btn-link pl-1 pt-1 text-info"
                          :title="$t('tenderview.remove-watched')"
                          @click="remove_watched(tender.id)">
                        <i class="far fa-eye-slash text-gray-dark"></i>
                      </button>
                    </span>
                  </p>

                  <div class="text-muted">
                    <span>{{ $t('tenderview.start') }}: <b>{{ tender.start | formatDateTime }}</b></span><br>
                    <span>{{ $t('tenderview.end') }}: <b>{{ tender.finish | formatDateTime }}</b></span><br>
                    <span>{{ $t('tenderview.created') }}: <b>{{ tender.creator_name }}</b></span><br>
                    <span v-if="tender.group_name">{{ $t('tenderview.group') }}: <b>{{ tender.group_name }}</b></span>
                    <br v-if="tender.group_name">
                    <span v-if="tender.warehouse_name">{{ $t('tenderview.warehouse') }}: <b> {{ tender.warehouse_name }}</b></span>
                    <br v-if="tender.warehouse_name">
                    <span v-if="tender.max_delivery">{{ $t('tender.maximum-workdays-delivery') }}: <b> {{ tender.max_delivery }}</b></span>
                    <br v-if="tender.max_delivery">
                    <span v-if="tender.approver_name != ''">{{ $t('tenderview.approver') }}: <b>{{ tender.approver_name}}</b></span>
                    <br v-if="tender.approver_name != ''">
                    <span v-if="tender.approved">{{ $t('tenderview.approved') }}: <b>{{ tender.approved | formatDateTime }}</b></span>
                  </div>
                  <h5 class="mt-2 mb-0" v-if="tender.additional_info">{{ $t('tenderview.additional-info') }}:</h5>
                  <div class="text-muted" v-if="tender.additional_info">
                    {{ tender.additional_info }}
                  </div>
                  <countdown :time="till_end" @end="finishTender" v-if="till_end > 0 && is_active">
                    <template slot-scope="props">
                      <span class="text-warning">
                        <strong>{{ $t('tenderview.proposal-time') }} </strong>
                        <strong v-if="props.days > 0">
                          {{ props.days }} {{ $t('tenderview.days') }}
                        </strong>
                        <strong v-if="props.hours > 0">
                          {{ props.hours }} {{ $t('tenderview.hours') }}
                        </strong>
                        <strong>
                          {{ props.minutes }} {{ $t('tenderview.minutes') }} {{ props.seconds }} {{ $t('tenderview.seconds') }}.
                        </strong>
                      </span>
                    </template>
                  </countdown>
                  <div class="text-muted" v-if="is_supplier">
                    <span v-if="tender.proposal_created">{{ $t('tenderview.proposal_created') }}:
                      <b>{{ tender.proposal_created | formatDateTime }}</b>
                    </span>
                  </div>
                  <div class="text-muted" v-if="is_supplier">
                    <span v-if="format_date_time(tender.proposal_created) !== format_date_time(tender.proposal_updated)">
                      {{ $t('tenderview.proposal_updated') }}: <b>{{ tender.proposal_updated | formatDateTime }}</b>
                    </span>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <div class="card card-default" v-if="is_supplier && is_active">
        <form @submit.prevent="add_proposal" class="form-modal form-proposal">
          <div class="card-body" v-if="tender.company_proposals !== ''">
          {{ $t('tenderview.proposal-done-by') }} {{ tender.company_proposals }}
          </div>
          <div class="card-body" v-if="tender.company_proposals === '' || tender.company_proposals_sharing_enabled ">
          <div class="table-responsive pb-5">
            <table class="table table-hover">
              <thead>
                <tr>
                  <th class="wd-xs">#</th>
                  <th class="wd-xxl">{{ $t('tenderview.name') }}</th>
                  <th class="wd-sd">{{ $t('details.transport-nr') }}</th>
                  <th class="wd-xs">{{ $t('tenderview.needed-qty') }}</th>
                  <th class="wd-xs">{{ $t('tenderview.unit') }}</th>
                  <th class="wd-sd">{{ $t('tenderview.part-producer') }}</th>
                  <th class="wd-sd">{{ $t('tenderview.product-code') }}</th>
                  <th class="wd-xs">{{ $t('tenderview.price-wo-vat') }}</th>
                  <th class="wd-md">{{ $t('tender.workdays-delivery') }}</th>
                  <th class="wd-xs">{{ $t('tenderview.total-qty') }}</th>
                  <th class="wd-xs">{{ $t('tenderview.amount-wo-wat') }}</th>
                  <th class="wd-xs"></th>
                </tr>
              </thead>
              <tbody>
                <tr :key="d.id" v-for="(d, index) in details">
                  <td>{{ index+1 }}</td>
                  <td>
                  <button
                    type="button"
                    class="btn btn-link p-0"
                    @click="open_modal(d.part_name, d.part_codes_tags, d.blacklisted_producers_info, d.part_comment)"
                    v-if="d.part_codes_tags || d.blacklisted_producers_info"
                  >
                    <i
                      :class="[d.part_comment ? 'fa fa-info-circle' : 'icon-info']"
                      :title="d.part_comment"
                    ></i>
                  </button>&nbsp;
                    {{ d.part_name }}
                </td>
                  <td>{{ d.customer_number }}</td>
                  <td>{{ d.qty }}</td>
                  <td>{{ d.part_unit_code }}</td>
                  <td v-if="till_end">
                    <div :key="p.id" v-for="p in d.proposals">
                      <div class="form-group mb-2">
                        <suggest
                          id="producer"
                          :required="false"
                          :classname="{'is-invalid': p.errors.producer, 'wd-sd': true, 'p-2': true}"
                          :value="p.producer.name"
                          :options="producers"
                          :index="p"
                          @search="search_producers"
                          @select="select_producer"
                          @clear="clear_producer"
                          @clear-options="clear_producer_options"
                        >
                        </suggest>
                        <ul class="parsley-errors-list filled" v-if="p.errors.producer">
                          <li class="parsley-required">{{ p.errors.producer[0] }}</li>
                        </ul>
                      </div>
                    </div>
                  </td>
                  <td v-if="!till_end">{{ d.proposals ? d.proposals[0].producer.name : '-' }}</td>
                  <td v-if="till_end">
                    <div v-for="(p, i) in d.proposals" :key="i">
                      <div class="inline">
                        <div class="form-group mb-2">
                          <input
                            class="form-control wd-sd p-2"
                            id="product_code"
                            v-bind:position="index"
                            v-bind:line="i"
                            maxlength="35"
                            v-model="p.product_code"
                            :class="{'is-invalid': p.errors.product_code}">
                        </div>
                      </div>
                      <ul class="parsley-errors-list filled" v-if="p.errors.product_code">
                        <li class="parsley-required">{{ p.errors.product_code[0] }}</li>
                      </ul>
                    </div>
                  </td>
                  <td v-if="!till_end">{{ d.proposals[0].product_code }}</td>
                  <td v-if="till_end">
                    <div :key="p.id" v-for="p in d.proposals">
                      <div class="form-group mb-2">
                        <currency-input
                            v-model="p.price"
                            class="form-control wd-xs p-2"
                            :precision="{min: 2, max: 4}"
                            :class="{'is-invalid': p.errors.price}"
                        />
                      </div>
                      <ul class="parsley-errors-list filled" v-if="p.errors.price">
                      <li class="parsley-required">{{ p.errors.price[0] }}</li>
                      </ul>
                    </div>
                  </td>
                  <td v-if="!till_end">{{ d.errors.price[0] }}</td>
                  <td v-if="till_end">
                    <div :key="p.id" v-for="p in d.proposals">
                      <div class="form-row">
                        <div class="form-group mb-2 float-left">
                          <input
                            v-model="p.delivery_days"
                            class="form-control wd-xs"
                            maxlength="5"
                            type="text"
                            inputmode="numeric"
                            :class="{'parsley-error': p.errors.delivery_days}"
                            @input="event => {
                                let v = event.target.value.replace(/[^0-9]/g, '')
                                const maxDelivery = tender.max_delivery || 32767
                                p.errors.delivery_days = undefined
                                if (Number(v) > maxDelivery) {
                                  p.errors.delivery_days = [`${$t('Maximum')}: ${maxDelivery}`]
                                }
                                p.delivery_days = v === 0 ? null : v
                              }"
                          >

                          <ul class="parsley-errors-list filled" v-if="p.errors.delivery_days">
                            <li class="parsley-required">{{ p.errors.delivery_days[0] }}</li>
                          </ul>
                        </div>
                      </div>
                    </div>
                  </td>
                  <td v-if="!till_end">{{ d.proposals[0].additional_qty / d.errors.delivery_date }}</td>
                  <td>
                    <div class="form-group mb-2" :key="p.id" v-for="p in d.proposals">
                      <p class="form-control-plaintext">{{ d.qty }}</p>
                    </div>
                  </td>
                  <td>
                    <div class="form-group mb-2" :key="p.id" v-for="p in d.proposals">
                      <p class="form-control-plaintext"> {{ sub_total(d.qty, p.price) }} {{ $t('global.euro-sign') }}</p>
                    </div>
                  </td>
                  <td>
                    <div class="form-group mb-2" :key="p.id" v-for="p in d.proposals">
                      <button type="button" class="icon-minus btn btn-link link-unstyled text-danger"
                      :title="$t('tenderview.delete_additional_proposal')"
                      v-if="till_end && d.proposals.length > 1"
                      @click="delete_additional_proposal(p, d.proposals)"></button>
                    </div>
                  </td>
                  <td>
                    <div class="btn-group">
                    <button
                      type="button"
                      class="icon-plus text-success link-unstyled btn btn-link p-1"
                      :title="$t('tenderview.add-proposal')"
                      v-if="till_end &&
                      d.proposals.length < 3 &&
                      is_mandatory_fields_set(d.proposals.slice(-1)[0])"
                      @click="append_additional_proposal(d)"></button>
                    <button
                      type="button"
                      class="icon-close link-unstyled btn btn-link p-1"
                      :title="$t('tenderview.delete-all')"
                      v-if="till_end && d.proposals.length > 0 "
                      @click="delete_all_additional_proposals(d)"></button>
                      <button
                       type="button"
                       class="btn btn-link link-unstyled p-1"
                       :title="$t('tenderview.detail_comment')"
                       @click="open_add_detail_comment_modal(d)"
                       data-toggle="modal"
                       data-target="#commentsModal">
                       <i class="icon-bubbles text-info"></i>
                       <span class="text-bold">({{d.supplier_comments.length > 0?1:0}})</span>
                      </button>
                    </div>
                  </td>
                </tr>
                <tr>
                  <td colspan="9"></td>
                  <td>
                    <b>{{ total_qty }}</b>
                  </td>
                  <td>
                    <b>{{ total }} {{ $t('global.euro-sign')}}</b>
                  </td>
                  <td v-if="till_end"></td>
                  <td>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <div class="form-group wd-xxl">
            <label>{{ $t('tenderview.comment') }}</label>
            <textarea
              v-model="comment.content"
              class="form-control"
              :placeholder="$t('tenderview.add-comment')"
              rows="3"
              v-if="till_end"
              type="text">
            </textarea>
            <div v-if="!is_active">{{ comment.content }}</div>
          </div>
<hr>
          <div class="form-group mb-0" v-if="is_supplier">
            <h4>{{ $t('tenderview.title.supplier-agreed') }}</h4>
              <input type="checkbox"
                 v-if="!tender.agreed"
                 @click="add_agreed(tender.id)"
                 :class="{'parsley-error': agree_error}">
              <input type="checkbox"
                 checked="checked"
                 v-if="tender.agreed"
                 @click="remove_agreed(tender.id)"
                 :class="{'parsley-error': agree_error}">
              <label class="mb-0">{{ $t('tenderview.supplier-agreed') }}</label>
          </div>

          <div class="form-group wd-xxl" v-if="is_supplier">
              <ul class="parsley-errors-list filled" v-if="agree_error">
                  <li class="parsley-required">{{ agree_error }}</li>
              </ul>
          </div>

          <button
            class="btn btn-success"
            type="submit"
            v-if="till_end"
            >{{ $t('tenderview.make-offer') }}
          </button>

          <button
            class="btn btn-outline-danger"
            type="button"
            v-if="till_end"
            @click="cancel_proposals">{{ $t('tenderview.cancel-offer') }}
          </button>

          <router-link
            :to="{name: 'tenders'}"
            class="btn btn-sm btn-secondary wd-xs"
            type="submit">
            <span>{{ $t('tenderview.cancel') }}</span>
          </router-link>

        </div>
        </form>
      </div>
      <div class="card card-default" v-if="is_supplier && (is_finished || is_choosing)">
        <div class="card-body">
          <div class="table-responsive pb-5">
            <table class="table table-hover">
              <thead>
              <tr>
                <th class="wd-xs">#</th>
                <th class="wd-xl">{{ $t('tenderview.name') }}</th>
                <th class="wd-sd">{{ $t('details.transport-nr') }}</th>
                <th class="wd-xs">{{ $t('tenderview.needed-qty') }}</th>
                <th class="wd-xs">{{ $t('tenderview.unit') }}</th>
                <th class="wd-sd">{{ $t('tenderview.price-wo-vat') }}</th>
                <th class="wd-md">{{ $t('tenderview.producer') }}</th>
                <th class="wd-sd">{{ $t('tenderview.product-code') }}</th>
                <th class="wd-xl">{{ $t('tender.workdays-delivery') }}</th>
                <th class="wd-sd">{{ $t('details.status') }}</th>
              </tr>
              </thead>
              <tbody>
              <tr :key="d.id" v-for="(d, index) in details">
                <td>{{ index+1 }}</td>
                <td>
                  <button
                    class="btn btn-link p-0"
                    @click="open_modal(d.part_name, d.part_codes_tags, d.blacklisted_producers_info, d.part_comment)"
                    v-if="d.part_codes_tags || d.blacklisted_producers_info"
                  >
                    <i
                      :class="[d.part_comment ? 'fa fa-info-circle' : 'icon-info']"
                      :title="d.part_comment"
                    ></i>
                  </button>&nbsp;
                  {{ d.part_name }}
                </td>
                <td>{{ d.customer_number }}</td>
                <td>{{ d.qty }}</td>
                <td>{{ d.part_unit_code }}</td>
                <td>
                  <div :key="'price_' + index" v-for="(p, index) in d.proposals">
                    <span
                      v-if="p && p.price > 0"
                      :class="{'text-bold text-green': get_winner(d, p)}"
                    >{{ p.price.toFixed(4).replace(/0{0,2}$/, '') }} {{ $t('global.euro-sign') }}
                    </span>
                  </div>
                </td>
                <td>
                  <div :key="'producer_' + index" v-for="(p, index) in d.proposals">
                    <span
                      v-if="p"
                      :class="{'text-bold text-green': get_winner(d, p)}"
                    >{{ p.producer.name }}</span>
                  </div>
                </td>
                <td>
                  <div :key="'product_code_' + index" v-for="(p, index) in d.proposals">
                    <span
                      v-if="p"
                      :class="{'text-bold text-green': get_winner(d, p)}"
                    >{{ p.product_code }}</span>
                  </div>
                </td>
                <td>
                  <div :key="'qty_' + index" v-for="(p, index) in d.proposals">
                    <div :class="{'text-bold text-green': get_winner(d, p)}">
                      <span v-if="p.delivery_days || p.delivery_days === 0">{{ p.delivery_days }}</span>
                      <span v-if="p.delivery_date">| {{ p.delivery_date | formatDate }}</span>
                    </div>
                  </div>
                </td>
                <td>
                  <div :class="supplierStatus(d.status, get_winner(d, d.proposals[0])).class">
                    {{ $t(`details.state.${supplierStatus(d.status, get_winner(d, d.proposals[0])).text}`.toLowerCase()) }}
                  </div>
                </td>
                <td>
                  <button
                    v-if="d.supplier_comments.length > 0 || d.winner_comment"
                    type="button"
                    class="btn btn-link link-unstyled p-1"
                    :title="$t('tenderview.detail_comment')"
                    @click="open_detail_comments_modal(d)"
                    data-toggle="modal"
                    data-target="#commentsModal">
                    <i class="icon-bubbles text-info"></i>
                    <span class="text-bold">({{d.supplier_comments.length + (d.winner_comment ? 1 : 0)}})</span>
                  </button>
                </td>
              </tr>
              </tbody>
            </table>
          </div>
          <div v-if="tender.comments_options && tender.comments_options.length > 0">
            <h4>{{ $t('tenderview.comment') }}</h4>
            <hr>
            <div
              class="comment"
              v-if="c.content"
              v-for="(c, index) in tender.comments_options"
              :key="'comment_' + index">
              <div class="table-responsive">
                <table class="table">
                  <tbody>
                  <tr>
                    <td class="b0 pl-0">
                      <p class="h4 mt-0">
                        <span class="text-bold">{{ c.supplier_name }}</span>
                      </p>
                      <div class="text-muted">{{ c.content }}</div>
                      <div>{{ c.created | formatDateTime }}</div>
                    </td>
                  </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="card card-default" v-if="is_supplier && is_ordered">
        <div class="card-body">
          <div class="table-responsive pb-5">
            <table class="table table-hover">
              <thead>
              <tr>
                <th class="wd-xs">#</th>
                <th class="wd-xl">{{ $t('tenderview.name') }}</th>
                <th class="wd-sd">{{ $t('details.transport-nr') }}</th>
                <th class="wd-xs">{{ $t('tenderview.needed-qty') }}</th>
                <th class="wd-xs">{{ $t('tenderview.unit') }}</th>
                <th class="wd-sd">{{ $t('tenderview.price-wo-vat') }}</th>
                <th class="wd-md">{{ $t('tenderview.producer') }}</th>
                <th class="wd-sd">{{ $t('tenderview.product-code') }}</th>
                <th class="wd-xl">{{ $t('tender.workdays-delivery') }}</th>
              </tr>
              </thead>
              <tbody>
              <tr :key="d.id" v-for="(d, index) in details">
                <td>{{ index+1 }}</td>
                <td>
                  <button
                    class="btn btn-link p-0"
                    @click="open_modal(d.part_name, d.part_codes_tags, d.blacklisted_producers_info, d.part_comment)"
                    v-if="d.part_codes_tags  || d.blacklisted_producers_info"
                  >
                    <i
                      :class="[d.part_comment ? 'fa fa-info-circle' : 'icon-info']"
                      :title="d.part_comment"
                    ></i>
                  </button>&nbsp;
                  {{ d.part_name }}
                </td>
                <td>{{ d.customer_number }}</td>
                <td>{{ d.qty }}</td>
                <td>{{ d.part_unit_code }}</td>
                <td>
                  <div :key="'price_' + index" v-for="(p, index) in d.proposals">
                    <span
                      v-if="p && p.price > 0"
                      :class="{'text-bold text-green': get_winner(d, p)}"
                    >{{ p.price.toFixed(4).replace(/0{0,2}$/, '') }} {{ $t('global.euro-sign') }}
                    </span>
                    <span v-else>-</span>
                  </div>
                </td>
                <td>
                  <div :key="'producer_' + index" v-for="(p, index) in d.proposals">
                    <span
                      v-if="p"
                      :class="{'text-bold text-green': get_winner(d, p)}"
                    >{{ p.producer.name }}</span>
                    <span v-else>-</span>
                  </div>
                </td>
                <td>
                  <div :key="'product_code_' + index" v-for="(p, index) in d.proposals">
                    <span
                      v-if="p"
                      :class="{'text-bold text-green': get_winner(d, p)}"
                    >{{ p.product_code }}
                    </span><span v-else>-</span>
                  </div>
                </td>
                <td>
                  <div :key="'qty_' + index" v-for="(p, index) in d.proposals">
                    <div :class="{'text-bold text-green': get_winner(d, p)}">
                      <span v-if="p.delivery_days || p.delivery_days === 0">{{ p.delivery_days }}</span>
                      <span v-if="p.delivery_date">| {{ p.delivery_date | formatDate }}</span>
                    </div>
                  </div>
                </td>
                <td>
                  <button
                    v-if="d.supplier_comments.length > 0 || d.winner_comment"
                    type="button"
                    class="btn btn-link link-unstyled p-1"
                    :title="$t('tenderview.detail_comment')"
                    @click="open_detail_comments_modal(d)"
                    data-toggle="modal"
                    data-target="#commentsModal">
                    <i class="icon-bubbles text-info"></i>
                    <span class="text-bold">({{d.supplier_comments.length + (d.winner_comment ? 1 : 0)}})</span>
                  </button>
                </td>
              </tr>
              </tbody>
            </table>
          </div>

          <hr/>
          <div v-if="tender.comments_options && tender.comments_options.length > 0">
            <h4>{{ $t('tenderview.comment') }}</h4>
            <hr>
            <div
              class="comment"
              v-if="c.content"
              v-for="(c, index) in tender.comments_options"
              :key="'comment_' + index">
              <div class="table-responsive">
                <table class="table">
                  <tbody>
                  <tr>
                    <td class="b0 pl-0">
                      <p class="h4 mt-0">
                        <span class="text-bold">{{ c.supplier_name }}</span>
                      </p>
                      <div class="text-muted">{{ c.content }}</div>
                      <div>{{ c.created | formatDateTime }}</div>
                    </td>
                  </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          <button
            v-if="tender.is_winner"
            class="btn btn-outline-info fa-pull-right ml-2"
            type="submit"
            @click="exportExcel(tender.id)">
            {{ $t('tenderview.save-xls') }} <i class="far fa-file-excel"></i>
          </button>

          <button
            v-if="tender.is_winner"
            class="btn btn-outline-info fa-pull-right"
            type="submit"
            @click="exportPDF(tender.id)">
            {{ $t('tenderview.save-pdf') }} <i class="far fa-file-pdf"></i>
          </button>

        </div>
      </div>
      <div class="card card-default" v-if="!is_supplier && is_active">
        <div class="card-body">
          <div class="table-responsive pb-5">
            <table class="table table-hover">
              <thead>
                <tr>
                  <th class="wd-xs">#</th>
                  <th class="wd-sd">{{ $t('tenderview.part-code') }}</th>
                  <th class="wd-xl">{{ $t('tenderview.name') }}</th>
                  <th class="wd-xs">{{ $t('details.transport-nr') }}</th>
                  <th class="wd-sd">{{ $t('details.customer_name') }}</th>
                  <th class="wd-xs">{{ $t('tenderview.needed-qty') }}</th>
                  <th class="wd-xs">{{ $t('tenderview.unit') }}</th>
                  <th class="wd-md">{{ $t('tenderview.supplier') }}</th>
                  <th class="wd-sd">{{ $t('tenderview.price-wo-vat') }}</th>
                  <th class="wd-sd">{{ $t('tenderview.producer') }}</th>
                  <th class="wd-xl">{{ $t('tender.workdays-delivery') }}</th>
                  <th class="wd-sd">{{ $t('details.creator') }}</th>
                  <th class="wd-sd">{{ $t('details.order_number') }}</th>
                  <th class="wd-xs">BC</th>
                </tr>
              </thead>
              <tbody>
                <tr :key="d.id" v-for="(d, index) in details">
                  <td>{{ index+1 }}</td>
                  <td>
                    {{ d.part_code }}
                  </td>
                  <td>
                    <button
                      class="btn btn-link p-0"
                      @click="open_modal(d.part_name, d.part_codes_tags, d.blacklisted_producers_info, d.part_comment)"
                      v-if="d.part_codes_tags || d.blacklisted_producers_info"
                    >
                      <i
                        :class="[d.part_comment ? 'fa fa-info-circle' : 'icon-info']"
                        :title="d.part_comment"
                      ></i>
                    </button>&nbsp;
                    {{ d.part_name }}
                  </td>
                  <td>{{ d.customer_number }}</td>
                  <td>{{ d.customer_name }}</td>
                  <td>{{ d.qty }}</td>
                  <td>{{ d.part_unit_code }}</td>
                  <td>
                    <div :key="'supplier_' + index" v-for="index in d.proposals[0].count">
                      <span>xxxx</span>
                    </div>
                  </td>
                  <td>
                    <div :key="'price_' + index" v-for="index in d.proposals[0].count">
                      <span>xx.xx {{ $t('global.euro-sign') }}</span>
                    </div>
                  </td>
                  <td>
                    <div :key="'producer_' + index" v-for="index in d.proposals[0].count">
                      <span>xxxx</span>
                    </div>
                  </td>
                  <td>
                    <div :key="'qty_' + index" v-for="index in d.proposals[0].count">
                      <span>xxxx</span>
                    </div>
                  </td>
                  <td>{{ d.creator_name }}</td>
                  <td>{{ d.order_number }}</td>
                  <td>
                    <b-c-detail-status
                      v-if="d.bc_id"
                      justify-content="left"
                      :detail="d"
                    />
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <div v-if="tender.comments_options && tender.comments_options.length > 0">
            <h4>{{ $t('tenderview.comment') }}</h4>
            <hr>
            <div
              class="comment"
              v-if="c.content"
              v-for="(c, index) in tender.comments_options"
              :key="'comment_' + index">
              <div class="table-responsive">
                <table class="table">
                  <tbody>
                  <tr>
                    <td class="b0 pl-0">
                      <p class="h4 mt-0">
                        <span class="text-bold">{{ c.supplier_name }}</span>
                      </p>
                      <div class="text-muted">xxxx</div>
                      <div>{{ c.created | formatDateTime }}</div>
                    </td>
                  </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          <hr>

          <button
            class="btn btn-outline-danger"
            @click="cancel_tender">{{ $t('tenderview.cancel-offer') }}
          </button>
        </div>
      </div>
      <div class="card card-default" v-if="!is_supplier && is_waiting">
        <div class="card-body">
          <div class="table-responsive pb-5">
            <table class="table table-hover">
              <thead>
                <tr>
                  <th class="wd-xs">#</th>
                  <th class="wd-sd">{{ $t('tenderview.part-code') }}</th>
                  <th class="wd-xl">{{ $t('tenderview.name') }}</th>
                  <th class="wd-xs">{{ $t('tenderview.transport-no') }}</th>
                  <th class="wd-sd">{{ $t('details.customer_name') }}</th>
                  <th class="wd-sd">{{ $t('details.creator') }}</th>
                  <th class="wd-sd">{{ $t('details.order_number') }}</th>
                  <th class="wd-xs">BC</th>
                  <th class="wd-xs">{{ $t('tenderview.unit') }}</th>
                  <th class="wd-xs">{{ $t('details.replenishment-qty') }}</th>
                  <th class="wd-xs">{{ $t('tender.order-qty') }}</th>
                </tr>
              </thead>
              <tbody>
              <tr :key="d.id" v-for="(d, index) in details">
                <td>{{ index+1 }}</td>
                <td>
                  {{ d.part_code }}
                </td>
                <td>
                  <button
                    class="btn btn-link p-0"
                    @click="open_modal(d.part_name, d.part_codes_tags, d.blacklisted_producers_info, d.part_comment)"
                    v-if="d.part_codes_tags || d.blacklisted_producers_info"
                  >
                    <i
                      :class="[d.part_comment ? 'fa fa-info-circle' : 'icon-info']"
                      :title="d.part_comment"
                    ></i>
                  </button>&nbsp;
                  {{ d.part_name }}
                </td>
                <td>{{ d.customer_number }}</td>
                <td>{{ d.customer_name }}</td>
                <td>{{ d.creator_name }}</td>
                <td>{{ d.order_number }}</td>
                <td>
                  <b-c-detail-status
                    v-if="d.bc_id"
                    justify-content="left"
                    :detail="d"
                  />
                </td>
                <td>{{ d.part_unit_code }}</td>
                <td>{{ d.replenishment_qty > 0 ? d.replenishment_qty : 0 }}</td>
                <td>{{ d.qty }}</td>
              </tr>
              </tbody>
            </table>
            <hr>
            <div class="text-right">
              <router-link :to="{name: 'tenders'}" class="btn btn-sm btn-secondary wd-xs" type="cancel">
                <span>{{ $t('tenderview.cancel') }}</span>
              </router-link>
              <router-link :to="{name: 'tender_edit', params: {id: tender.id}}" class="btn btn-sm btn-warning" type="submit">
                <span>{{ $t('tenderview.edit') }}</span>
              </router-link>
            </div>
          </div>
        </div>
      </div>
      <div class="card card-default" v-if="!is_supplier && is_finished">
        <div class="card-body">
          <div class="table-responsive pb-5">
            <table class="table table-hover">
              <thead>
              <tr>
                <th class="wd-xs">#</th>
                <th class="wd-sm">{{ $t('tenderview.part-code') }}</th>
                <th class="wd-xl">{{ $t('tenderview.name') }}</th>
                <th class="wd-xs">{{ $t('tenderview.transport-no') }}</th>
                <th class="wd-sd">{{ $t('details.customer_name') }}</th>
                <th class="wd-xs">{{ $t('tenderview.needed-qty') }}</th>
                <th class="wd-xs">{{ $t('tenderview.unit') }}</th>
                <th class="wd-sd">{{ $t('tenderview.offer') }}</th>
                <th class="wd-sd">{{ $t('tenderview.price-wo-vat') }}</th>
                <th class="wd-sd">{{ $t('tenderview.producer') }}</th>
                <th class="wd-sd">{{ $t('tenderview.product-code') }}</th>
                <th class="wd-xxl">{{ $t('tender.workdays-delivery') }}</th>
                <th class="wd-xs"></th>
                <th class="wd-sd">{{ $t('details.creator') }}</th>
                <th class="wd-sd">{{ $t('details.order_number') }}</th>
                <th class="wd-xs">BC</th>
              </tr>
              </thead>
              <tbody>
              <tr :key="d.id" v-for="(d, index) in details">
                <td>{{ index+1 }}</td>
                <td>
                  {{ d.part_code }}
                  <button v-if="is_user_able_to_blacklist" class="btn btn-link p-0" :title="$t('blacklists.add')" @click="open_add_to_black_list_modal(d)">
                    <i class="fa icon-ban link-unstyled"></i>
                  </button>
                </td>
                <td>
                  <button
                    class="btn btn-link p-0"
                    @click="open_modal(d.part_name, d.part_codes_tags, d.blacklisted_producers_info, d.part_comment)"
                    v-if="d.part_codes_tags || d.blacklisted_producers_info"
                  >
                    <i
                      :class="[d.part_comment ? 'fa fa-info-circle' : 'icon-info']"
                      :title="d.part_comment"
                    ></i>
                  </button>&nbsp;
                  {{ d.part_name }}
                </td>
                <td>{{ d.customer_number }}</td>
                <td>{{ d.customer_name }}</td>
                <td>{{ d.qty }}</td>
                <td>{{ d.part_unit_code }}</td>
                <td>
                  <div :key="'supplier_' + index" v-for="(p, index) in number_of_lines(d.proposals, d.lines)">
                    <span v-if="p">{{ p.supplier_name }}</span>
                  </div>
                  <button
                    class="btn btn-link btn-sm pl-0"
                    v-if="d.proposals.length > 3 && d.lines <= 3"
                    @click="show_more(d)">{{ $t('tenderview.more') }} ...
                  </button>
                  <button
                    class="btn btn-link btn-sm pl-0"
                    v-if="d.proposals.length > 3 && d.lines > 3"
                    @click="show_less(d)">{{ $t('tenderview.less') }} ...
                  </button>
                </td>
                <td>
                  <div :key="'price_' + index" v-for="(p, index) in number_of_lines(d.proposals, d.lines)">
                    <span v-if="p && p.price > 0">
                      {{ p.price.toFixed(4).replace(/0{0,2}$/, '') }} {{ $t('global.euro-sign') }}
                    </span>
                  </div>
                  <button
                    class="btn btn-link btn-sm pl-0"
                    v-if="d.proposals.length > 3 && d.lines <= 3"
                    @click="show_more(d)">{{ $t('tenderview.more') }} ...
                  </button>
                  <button
                    class="btn btn-link btn-sm pl-0"
                    v-if="d.proposals.length > 3 && d.lines > 3"
                    @click="show_less(d)">{{ $t('tenderview.less') }} ...
                  </button>
                </td>
                <td>
                  <div :key="'producer_' + index" v-for="(p, index) in number_of_lines(d.proposals, d.lines)">
                    <span v-if="p">{{ p.producer.name }}</span>
                  </div>
                  <button
                    class="btn btn-link btn-sm pl-0"
                    v-if="d.proposals.length > 3 && d.lines <= 3"
                    @click="show_more(d)">{{ $t('tenderview.more') }} ...
                  </button>
                  <button
                    class="btn btn-link btn-sm pl-0"
                    v-if="d.proposals.length > 3 && d.lines > 3"
                    @click="show_less(d)">{{ $t('tenderview.less') }} ...
                  </button>
                </td>
                <td>
                  <div :key="'product_code_' + index" v-for="(p, index) in number_of_lines(d.proposals, d.lines)">
                    <span v-if="p">{{ p.product_code }}</span>
                  </div>
                  <button
                    class="btn btn-link btn-sm pl-0"
                    v-if="d.proposals.length > 3 && d.lines <= 3"
                    @click="show_more(d)">{{ $t('tenderview.more') }} ...
                  </button>
                  <button
                    class="btn btn-link btn-sm pl-0"
                    v-if="d.proposals.length > 3 && d.lines > 3"
                    @click="show_less(d)">{{ $t('tenderview.less') }} ...
                  </button>
                </td>
                <td>
                  <div :key="'qty_' + index" v-for="(p, index) in number_of_lines(d.proposals, d.lines)">
                    <div v-if="p">
                      <span v-if="p.delivery_days || p.delivery_days === 0">{{ p.delivery_days }}</span>
                      <span v-if="p.delivery_date">| {{ p.delivery_date | formatDate }}</span>
                    </div>
                  </div>
                  <button
                    class="btn btn-link btn-sm pl-0"
                    v-if="d.proposals.length > 3 && d.lines <= 3"
                    @click="show_more(d)">{{ $t('tenderview.more') }} ...
                  </button>
                  <button
                    class="btn btn-link btn-sm pl-0"
                    v-if="d.proposals.length > 3 && d.lines > 3"
                    @click="show_less(d)">{{ $t('tenderview.less') }} ...
                  </button>
                </td>
                <td>
                  <div class="btn-group">
                    <button v-if="d.supplier_comments.length > 0"
                     class="btn btn-link link-unstyled p-1"
                     :title="$t('tenderview.show-comments')"
                     @click="open_detail_comments_modal(d)"
                     data-toggle="modal"
                     data-target="#commentsModal">
                     <i class="icon-bubbles text-info"></i>
                     <span class="text-bold">({{d.supplier_comments.length}})</span>
                    </button>
                   </div>
                </td>
                <td>{{ d.creator_name }}</td>
                <td>{{ d.order_number }}</td>
                <td>
                    <b-c-detail-status
                      v-if="d.bc_id"
                      justify-content="left"
                      :detail="d"
                    />
                  </td>
              </tr>
              </tbody>
            </table>

            <button
              class="btn btn-success fa-pull-right"
              type="submit"
              @click="start_choosing">{{ $t('tenderview.start-choosing') }}
            </button>
          </div>

          <div v-if="tender.comments_options && tender.comments_options.length > 0">
            <h4>{{ $t('tenderview.comment') }}</h4>
            <hr>
            <div
              class="comment"
              v-if="c.content"
              v-for="(c, index) in tender.comments_options"
              :key="'comment_' + index">
              <div class="table-responsive">
                <table class="table">
                  <tbody>
                  <tr>
                    <td class="b0 pl-0">
                      <p class="h4 mt-0">
                        <span class="text-bold">{{ c.supplier_name }}</span>
                      </p>
                      <div class="text-muted">{{ c.content }}</div>
                      <div>{{ c.created | formatDateTime }}</div>
                    </td>
                  </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="col-xl-12 text-center" v-if="loader">
        <div class="card">
          <div class="card-body loader-demo d-flex align-items-center justify-content-center">
            <div class="ball-beat">
              <div></div>
              <div></div>
              <div></div>
            </div>
          </div>
        </div>
      </div>
      <div class="card card-default" v-if="!is_supplier && is_choosing && !loader">
        <div class="card-body">
          <div class="table-responsive pb-5">
            <table class="table table-hover">
              <thead>
                <tr>
                  <th class="wd-xs">#</th>
                  <th class="wd-sd">{{ $t('tenderview.part-code') }}, {{ $t('tenderview.name') }}</th>
                  <th class="wd-xs">{{ $t('tenderview.transport-no') }}</th>
                  <th class="wd-sd">{{ $t('details.customer_name') }}</th>
                  <th class="wd-xs">{{ $t('tenderview.needed-qty') }}</th>
                  <th class="wd-xs">{{ $t('tenderview.unit') }}</th>
                  <th class="wd-auto">{{ $t('tenderview.offer') }}</th>
                  <th class="wd-xs">{{ $t('tenderview.price-wo-vat') }}</th>
                  <th class="wd-xs">{{ $t('tenderview.producer') }}</th>
                  <th class="wd-sd">{{ $t('tenderview.product-code') }}</th>
                  <th class="wd-xs">{{ $t('tender.workdays-delivery') }}</th>
                  <th class="wd-xxl-440">{{ $t('tenderview.winner') }}</th>
                  <th class="wd-sd">{{ $t('details.creator') }}</th>
                  <th class="wd-sd">{{ $t('details.order_number') }}</th>
                  <th class="wd-xs">BC</th>
                </tr>
              </thead>
              <tbody>
              <tr :key="d.id" v-for="(d, index) in details">
                <td>{{ index+1 }}</td>
                <td>
                  <div class="wd-sd">
                    <span class="pr-1">
                      {{ d.part_code }}
                    </span>
                    <button
                        v-if="is_user_able_to_blacklist"
                        class="btn btn-link p-0"
                        :title="$t('blacklists.add')"
                        @click="open_add_to_black_list_modal(d)">
                      <i class="fa icon-ban link-unstyled"></i>
                    </button>
                  </div>
                  <div class="wd-sd">
                    <button
                        class="btn btn-link p-0"
                        @click="open_modal(d.part_name, d.part_codes_tags, d.blacklisted_producers_info, d.part_comment)"
                        v-if="d.part_codes_tags || d.blacklisted_producers_info">
                      <i
                        :class="[d.part_comment ? 'fa fa-info-circle' : 'icon-info']"
                        :title="d.part_comment"
                      ></i>
                    </button>&nbsp;
                    {{ d.part_name }}
                  </div>
                </td>
                <td>{{ d.customer_number }}</td>
                <td>{{ d.customer_name }}</td>
                <td>{{ d.qty }}</td>
                <td>{{ d.part_unit_code }}</td>
                <td v-if="!is_supplier">
                  <div class="wd-auto text-nowrap" :key="'supplier_' + index" v-for="(p, index) in number_of_lines(d.proposals, d.lines)">
                    <span v-if="p && (!d.updateProposal || d.updateProposal === p.id)">{{ p.supplier_name }} ({{ p.user__email }})</span>
                  </div>
                  <template v-if="!d.updateProposal">
                    <button
                      class="btn btn-link btn-sm pl-0"
                      v-if="d.proposals.length > 3 && d.lines <= 3"
                      @click="show_more(d)">{{ $t('tenderview.more') }} ...
                    </button>
                    <button
                      class="btn btn-link btn-sm pl-0"
                      v-if="d.proposals.length > 3 && d.lines > 3"
                      @click="show_less(d)">{{ $t('tenderview.less') }} ...
                    </button>
                  </template>
                </td>
                <td>
                  <div class="wd-auto text-nowrap" :key="'price_' + index" v-for="(p, index) in number_of_lines(d.proposals, d.lines)">
                    <span v-if="p && p.price > 0 && (!d.updateProposal || d.updateProposal === p.id)">
                      {{ p.price.toFixed(4).replace(/0{0,2}$/, '') }} {{ $t('global.euro-sign') }}
                    </span>
                  </div>
                  <template v-if="!d.updateProposal">
                    <button
                      class="btn btn-link btn-sm pl-0"
                      v-if="d.proposals.length > 3 && d.lines <= 3"
                      @click="show_more(d)">...
                    </button>
                    <button
                      class="btn btn-link btn-sm pl-0"
                      v-if="d.proposals.length > 3 && d.lines > 3"
                      @click="show_less(d)">...
                    </button>
                  </template>
                </td>
                <td>
                  <div class="wd-auto text-nowrap" :key="'producer_' + index" v-for="(p, index) in number_of_lines(d.proposals, d.lines)">
                    <template v-if="p && p.producer.name">
                      <template v-if="!d.updateProposal">
                        <router-link
                          target="_blank"
                          :to="{name: 'producer_edit', params: {id: p.producer.id}}"
                        >{{ p.producer.name }}</router-link>
                        <button
                          class="btn btn-link px-1 pt-0 pb-1"
                          :title="$t('button.edit')"
                          @click="d.updateProposal = p.id; p.newProducer = p.producer"
                        >
                          <i class="far fa-edit"></i>
                        </button>
                      </template>

                      <div
                        v-if="d.updateProposal === p.id"
                        class="form-group mb-2"
                      >
                        <suggest
                          id="producer"
                          required
                          :classname="{'is-invalid': p.errors.producer, 'wd-sd': true, 'p-2': true}"
                          :value="p.newProducer.name"
                          :options="producers"
                          :index="p"
                          @search="search_producers"
                          @select="items => select_admin_producer(items, d)"
                          @clear-options="clear_producer_options"
                        ></suggest>
                        <ul class="parsley-errors-list filled" v-if="p.errors.producer">
                          <li class="parsley-required">{{ p.errors.producer[0] }}</li>
                        </ul>

                        <div class="mt-1 d-flex flex-row justify-content-between">
                          <button
                            class="btn btn-sm btn-warning"
                            @click="d.updateProposal = null"
                          >{{ $t('button.cancel') }}</button>

                          <button
                            class="btn btn-sm btn-success"
                            @click="updateProposal(p, d)"
                          >{{ $t('button.save') }}</button>
                        </div>
                      </div>
                    </template>
                  </div>
                  <template v-if="!d.updateProposal">
                    <button
                      class="btn btn-link btn-sm pl-0"
                      v-if="d.proposals.length > 3 && d.lines <= 3"
                      @click="show_more(d)">...
                    </button>
                    <button
                      class="btn btn-link btn-sm pl-0"
                      v-if="d.proposals.length > 3 && d.lines > 3"
                      @click="show_less(d)"> ...
                    </button>
                  </template>
                </td>
                <td>
                  <div class="wd-auto text-nowrap" :key="'product_code_' + index" v-for="(p, index) in number_of_lines(d.proposals, d.lines)">
                    <span v-if="p && (!d.updateProposal || d.updateProposal === p.id)">{{ p.product_code }}</span>
                  </div>
                  <template v-if="!d.updateProposal">
                    <button
                      class="btn btn-link btn-sm pl-0"
                      v-if="d.proposals.length > 3 && d.lines <= 3"
                      @click="show_more(d)">...
                    </button>
                    <button
                      class="btn btn-link btn-sm pl-0"
                      v-if="d.proposals.length > 3 && d.lines > 3"
                      @click="show_less(d)"> ...
                    </button>
                  </template>
                </td>
                <td>
                  <div class="wd-auto text-nowrap" :key="'qty_' + index" v-for="(p, index) in number_of_lines(d.proposals, d.lines)">
                    <div v-if="p && (!d.updateProposal || d.updateProposal === p.id)">
                      <span v-if="p.delivery_days || p.delivery_days === 0">{{ p.delivery_days }}</span>
                      <span v-if="p.delivery_date">| {{ p.delivery_date | formatDate }}</span>
                    </div>
                  </div>
                  <template v-if="!d.updateProposal">
                    <button
                      class="btn btn-link btn-sm pl-0"
                      v-if="d.proposals.length > 3 && d.lines <= 3"
                      @click="show_more(d)">...
                    </button>
                    <button
                      class="btn btn-link btn-sm pl-0"
                      v-if="d.proposals.length > 3 && d.lines > 3"
                      @click="show_less(d)">...
                    </button>
                  </template>
                </td>

                <td>
                  <div class="form-group row mb-1" :key="w + '_' + idx" v-for="(w, idx) in d.winners">
                    <div class="wd-sd p-0 float-left mr-1" v-if="has_proposals(d)">
                      <select
                        @change="winner_proposal_selected(d, $event)"
                        class="form-control"
                        id="winner_name"
                        v-model="w.name"
                        :class="{'parsley-error': w.errors && w.errors.supplier}"
                        :disabled="d.status === 'ORDERED'"
                      >
                        <option value="">-- {{ $t('tenderview.choose-winner') }}--</option>
                        <option
                          :value="p.id"
                          v-for="(p, index) in d.proposals"
                          :key="d.id + '_' + index"
                          :hidden ="d.winners.find(winner => winner.name === p.id) && p.id != w.name">
                          {{ p.supplier_name }} - {{p.producer.name}}
                        </option>
                      </select>
                      <ul class="parsley-errors-list filled" v-if="w.errors && w.errors.supplier">
                        <li class="parsley-required">{{ w.errors.supplier[0] }}</li>
                      </ul>
                    </div>

                    <div class="wd-sm p-0 float-left">
                      <select
                        v-if="has_proposals(d)"
                        @change="winner_reason_selected(w, $event)"
                        :class="{'parsley-error': w.errors && w.errors.reason}"
                        class="form-control"
                        id="winner_reason"
                        v-model="w.reason"
                        :disabled="d.status === 'ORDERED'"
                      >
                        <option value="">-- {{ $t('tenderview.choose-reason') }} --</option>
                        <option value="PRICE">{{ $t('tenderview.price') }}</option>
                        <option value="DUE DATE">{{ $t('tenderview.due-date') }}</option>
                      </select>
                      <ul class="parsley-errors-list filled" v-if="w.errors && w.errors.reason">
                        <li class="parsley-required">{{ w.errors.reason[0] }}</li>
                      </ul>
                    </div>
                    <div
                      v-if="has_proposals(d)"
                      class="wd-sd p-1 d-flex flex-row"
                    >
                      <button
                        class="btn btn-link link-unstyled p-1"
                        :title="d.commentInput ? 'Eemalda kommentaar' : 'Lisa kommentaar'"
                        :disabled="d.status === 'ORDERED'"
                        @click="toggleWinnerComment(d)"
                      >
                        <i class="icon-bubble text-success"></i>
                      </button>

                      <button
                        v-if="d.supplier_comments.length > 0 || d.winner_comment"
                        class="btn btn-link link-unstyled p-1"
                        :title="$t('tenderview.show-comments')"
                        @click="open_detail_comments_modal(d)"
                        data-toggle="modal"
                        data-target="#commentsModal">
                        <i class="icon-bubbles text-info"></i>
                        <span class="text-bold">({{d.supplier_comments.length + (d.winner_comment ? 1 : 0)}})</span>
                      </button>

                      <button
                        v-if="d.commentInput"
                        class="btn btn-xs btn-outline-success ml-auto"
                        :disabled="d.commentLoading"
                        @click="saveWinnerComment(d, true)"
                      >{{ $t('tender.save') }}</button>
                    </div>
                  </div>

                  <div
                    v-if="d.commentInput"
                    class="form-group row p-0 mt-0"
                  >
                    <div class="wd-sd">
                      <textarea
                        class="form-control"
                        :class="{'parsley-error': d.errors && d.errors.winner_comment}"
                        rows="3"
                        v-model="d.comment"
                        :placeholder="$t('tenderview.insert-comment')">
                      </textarea>
                      <ul class="parsley-errors-list filled" v-if="d.errors && d.errors.winner_comment">
                        <li class="parsley-required">{{ d.errors && d.errors.winner_comment[0] }}</li>
                      </ul>
                    </div>
                  </div>
                </td>
                <td>{{ d.creator_name }}</td>
                <td>{{ d.order_number }}</td>
                <td>
                  <b-c-detail-status
                    v-if="d.bc_id"
                    justify-content="left"
                    :detail="d"
                  />
                </td>
              </tr>
              </tbody>
            </table>

            <div class="form-group wd-xxl">
              <label>{{ $t('tenderview.comment-title') }}</label>
              <textarea
                v-model="tender.input_approver_comment.content"
                class="form-control"
                rows="3"
                type="text"
                :placeholder="$t('tenderview.insert-comment')"
                :disabled="approveCommentLoading"
              >
              </textarea>

              <div class="text-right mt-2">
                <button
                  class="btn btn-success"
                  :disabled="approveCommentLoading"
                  @click="update_approver_comment"
                >{{ $t('button.save') }}</button>
              </div>
            </div>


            <button
              :disabled="loader || disableConfirmBtn"
              class="btn btn-success fa-pull-right"
              @click="submitWinners"
            >{{ $tc('tenderview.confirm-winners', selectedWinners.count, {count: selectedWinners.count}) }}
            </button>
          </div>

          <div v-if="tender.comments_options && tender.comments_options.length > 0">
            <h4>{{ $t('tenderview.comment') }}</h4>
            <hr>
            <div
              class="comment"
              v-if="c.content"
              v-for="(c, index) in tender.comments_options"
              :key="'comment_' + index">
              <div class="table-responsive">
                <table class="table">
                  <tbody>
                  <tr>
                    <td class="b0 pl-0">
                      <p class="h4 mt-0">
                        <span class="text-bold">{{ c.supplier_name }}</span>
                      </p>
                      <div class="text-muted">{{ c.content }}</div>
                      <div>{{ c.created | formatDateTime }}</div>
                    </td>
                  </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>

        </div>
      </div>
      <div class="card card-default" v-if="!is_supplier && is_ordered">
        <div class="card-body">
          <div class="table-responsive pb-5">
            <table class="table table-hover">
              <thead>
              <tr>
                <th class="wd-xs">#</th>
                <th class="wd-sm">{{ $t('tenderview.part-code') }}</th>
                <th class="wd-xl">{{ $t('tenderview.name') }}</th>
                <th class="wd-xs">{{ $t('tenderview.transport-no') }}</th>
                <th class="wd-sd">{{ $t('details.customer_name') }}</th>
                <th class="wd-xs">{{ $t('tenderview.needed-qty') }}</th>
                <th class="wd-xs">{{ $t('tenderview.unit') }}</th>
                <th class="wd-sd">{{ $t('tenderview.offer') }}</th>
                <th class="wd-sd">{{ $t('tenderview.price-wo-vat') }}</th>
                <th class="wd-sd">{{ $t('tenderview.producer') }}</th>
                <th class="wd-sd">{{ $t('tenderview.product-code') }}</th>
                <th class="wd-xxl">{{ $t('tender.workdays-delivery') }}</th>
                <th class="wd-sd">{{ $t('details.creator') }}</th>
                <th class="wd-sd">{{ $t('details.status') }}</th>
                <th class="wd-sd">{{ $t('details.order_number') }}</th>
                <th class="wd-xs">BC</th>
              </tr>
              </thead>
              <tbody>
              <tr :key="d.id" v-for="(d, index) in details">
                <td>{{ index+1 }}</td>
                <td>
                    {{ d.part_code }}
                </td>
                <td>
                  <button
                    class="btn btn-link p-0"
                    @click="open_modal(d.part_name, d.part_codes_tags, d.blacklisted_producers_info, d.part_comment)"
                    v-if="d.part_codes_tags || d.blacklisted_producers_info"
                  >
                    <i
                      :class="[d.part_comment ? 'fa fa-info-circle' : 'icon-info']"
                      :title="d.part_comment"
                    ></i>
                  </button>&nbsp;
                  {{ d.part_name }}
                </td>
                <td>{{ d.customer_number }}</td>
                <td>{{ d.customer_name }}</td>
                <td>{{ d.qty }}</td>
                <td>{{ d.part_unit_code }}</td>
                <td>
                  <div :key="w+w_idx" v-for="(w, w_idx) in d.winners_data">
                    {{ w.supplier_name ? `${w.supplier_name} (${w.user__email})` : '-' }}
                  </div>
                </td>
                <td>
                  <div :key="w+w_idx" v-for="(w, w_idx) in d.winners_data">
                    {{ w.price ? w.price.toFixed(4).replace(/0{0,2}$/, '') : '-' }}
                  </div>
                </td>
                <td>
                  <div :key="w+w_idx" v-for="(w, w_idx) in d.winners_data">
                    {{ w.producer ? w.producer.name : '-'}}
                  </div>
                </td>
                <td>
                  <div :key="w+w_idx" v-for="(w, w_idx) in d.winners_data">
                    {{ w.product_code ? w.product_code : '-'}}
                  </div>
                </td>
                <td>
                  <div :key="w+w_idx" v-for="(w, w_idx) in d.winners_data">
                    <span v-if="w.delivery_days || w.delivery_days === 0">{{ w.delivery_days }}</span>
                    <span v-if="w.delivery_date">| {{ w.delivery_date | formatDate }}</span>
                  </div>
                </td>
                <td>{{ d.creator_name }}</td>
                <td>
                  <div :class="statusClass(d.status)">
                    {{ $t(`details.state.${d.status}`.toLowerCase()) }}
                  </div>
                </td>
                <td>{{ d.order_number }}</td>
                <td>
                  <b-c-detail-status
                    v-if="d.bc_id"
                    justify-content="left"
                    :detail="d"
                  />
                </td>
                <td>
                  <div class="btn-group">
                    <button v-if="d.supplier_comments.length > 0 || d.winner_comment"
                      class="btn btn-link link-unstyled p-1"
                      :title="$t('tenderview.show-comments')"
                      @click="open_detail_comments_modal(d)"
                      data-toggle="modal"
                      data-target="#commentsModal">
                      <i class="icon-bubbles text-info"></i>
                      <span class="text-bold" v-if="d.supplier_comments.length > 0">({{d.supplier_comments.length}})</span>
                      <span class="text-success" v-if="d.winner_comment">(1)</span>
                    </button>
                  </div>
                </td>
              </tr>
              </tbody>
            </table>
          </div>
          <div v-if="tender.comments_options && tender.comments_options.length > 0">
            <h4>{{ $t('tenderview.comment') }}</h4>
            <hr>
            <div
              class="comment"
              v-if="c.content"
              v-for="(c, index) in tender.comments_options"
              :key="'comment_' + index">
              <div class="table-responsive">
                <table class="table">
                  <tbody>
                  <tr>
                    <td class="b0 pl-0">
                      <p class="h4 mt-0">
                        <span class="text-bold">{{ c.supplier_name }}</span>
                      </p>
                      <div class="text-muted">{{ c.content }}</div>
                      <div>{{ c.created | formatDateTime }}</div>
                    </td>
                  </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          <hr/>
          <comment :tender="tender" v-if="tender.approver_comment" />

          <router-link
            :to="{name: 'tender_history', params: {id: tender.id}}"
            target="_blank"
            class="btn btn-outline-warning fa-pull-right ml-2">
            {{ $t('tenderview.show-proposals') }} <i class="fas fa-history"></i>
          </router-link>

          <button
            class="btn btn-outline-info fa-pull-right ml-2"
            type="submit"
            @click="exportExcel(tender.id)">
            {{ $t('tenderview.save-xls') }} <i class="far fa-file-excel"></i>
          </button>

          <button
            class="btn btn-outline-info fa-pull-right"
            type="submit"
            @click="exportPDF(tender.id)">
            {{ $t('tenderview.save-pdf') }} <i class="far fa-file-pdf"></i>
          </button>

        </div>
      </div>
      <div class="card card-default" v-if="is_canceled || is_failed">
        <div class="card-body">
          <div class="table-responsive pb-5">
            <table class="table table-hover">
              <thead>
              <tr>
                <th class="wd-xs">#</th>
                <th v-if="!is_supplier" class="wd-sm">{{ $t('tenderview.part-code') }}</th>
                <th class="wd-xl">{{ $t('tenderview.name') }}</th>
                <th v-if="!is_supplier" class="wd-xs">{{ $t('tenderview.transport-no') }}</th>
                <th class="wd-md">{{ $t('details.customer_name') }}</th>
                <th class="wd-xs">{{ $t('tenderview.needed-qty') }}</th>
                <th class="wd-xs">{{ $t('tenderview.unit') }}</th>
                <th v-if="!is_supplier" class="wd-sd">{{ $t('tenderview.offer') }}</th>
                <th class="wd-sd">{{ $t('tenderview.price-wo-vat') }}</th>
                <th class="wd-sd">{{ $t('tenderview.producer') }}</th>
                <th class="wd-sd">{{ $t('tenderview.product-code') }}</th>
                <th class="wd-xxl">{{ $t('tender.workdays-delivery') }}</th>
                <th v-if="!is_supplier" class="wd-sd">{{ $t('details.creator') }}</th>
                <th v-if="!is_supplier" class="wd-sd">{{ $t('details.status') }}</th>
                <th v-if="!is_supplier" class="wd-sd">{{ $t('details.order_number') }}</th>
                <th v-if="!is_supplier" class="wd-xs">BC</th>
              </tr>
              </thead>
              <tbody>
                <tr :key="d.id" v-for="(d, index) in details">
                  <td>{{ index+1 }}</td>
                  <td v-if="!is_supplier">
                      {{ d.part_code }}
                  </td>
                  <td>
                    <button
                      class="btn btn-link p-0"
                      @click="open_modal(d.part_name, d.part_codes_tags, d.blacklisted_producers_info, d.part_comment)"
                      v-if="d.part_codes_tags || d.blacklisted_producers_info"
                    >
                      <i
                        :class="[d.part_comment ? 'fa fa-info-circle' : 'icon-info']"
                        :title="d.part_comment"
                      ></i>
                    </button>&nbsp;
                    {{ d.part_name }}
                  </td>
                  <td v-if="!is_supplier">{{ d.customer_number }}</td>
                  <td>{{ d.customer_name }}</td>
                  <td>{{ d.qty }}</td>
                  <td>{{ d.part_unit_code }}</td>
                  <td v-if="!is_supplier">
                    <div :key="'supplier_' + index" v-for="(p, index) in number_of_lines(d.proposals, d.lines)">
                      <span v-if="p">{{ p.supplier_name }}</span>
                    </div>
                    <button
                     class="btn btn-link btn-sm pl-0"
                     v-if="d.proposals.length > 3 && d.lines <= 3"
                     @click="show_more(d)">{{ $t('tenderview.more') }} ...
                    </button>
                    <button
                     class="btn btn-link btn-sm pl-0"
                     v-if="d.proposals.length > 3 && d.lines > 3"
                     @click="show_less(d)">{{ $t('tenderview.less') }} ...
                    </button>
                  </td>
                  <td>
                    <div :key="'price_' + index" v-for="(p, index) in number_of_lines(d.proposals, d.lines)">
                      <span v-if="p && p.price > 0">
                        {{ p.price.toFixed(4).replace(/0{0,2}$/, '') }} {{ $t('global.euro-sign') }}
                      </span>
                    </div>
                    <button
                      class="btn btn-link btn-sm pl-0"
                      v-if="d.proposals.length > 3 && d.lines <= 3"
                      @click="show_more(d)">{{ $t('tenderview.more') }} ...
                    </button>
                    <button
                      class="btn btn-link btn-sm pl-0"
                      v-if="d.proposals.length > 3 && d.lines > 3"
                      @click="show_less(d)">{{ $t('tenderview.less') }} ...
                    </button>
                  </td>
                  <td>
                    <div :key="'producer_' + index" v-for="(p, index) in number_of_lines(d.proposals, d.lines)">
                      <span v-if="p">{{ p.producer.name }}</span>
                    </div>
                    <button
                     class="btn btn-link btn-sm pl-0"
                     v-if="d.proposals.length > 3 && d.lines <= 3"
                     @click="show_more(d)">{{ $t('tenderview.more') }} ...
                    </button>
                    <button
                     class="btn btn-link btn-sm pl-0"
                     v-if="d.proposals.length > 3 && d.lines > 3"
                     @click="show_less(d)">{{ $t('tenderview.less') }} ...
                    </button>
                  </td>
                  <td>
                    <div :key="'product_code_' + index" v-for="(p, index) in number_of_lines(d.proposals, d.lines)">
                      <span v-if="p">{{ p.product_code }}</span>
                    </div>
                    <button
                     class="btn btn-link btn-sm pl-0"
                     v-if="d.proposals.length > 3 && d.lines <= 3"
                     @click="show_more(d)">{{ $t('tenderview.more') }} ...
                    </button>
                    <button
                     class="btn btn-link btn-sm pl-0"
                     v-if="d.proposals.length > 3 && d.lines > 3"
                     @click="show_less(d)">{{ $t('tenderview.less') }} ...
                    </button>
                  </td>
                  <td>
                    <div :key="'qty_' + index" v-for="(p, index) in number_of_lines(d.proposals, d.lines)">
                      <div v-if="p">
                        <span v-if="p.delivery_days || p.delivery_days === 0">{{ p.delivery_days }}</span>
                        <span v-if="p.delivery_date">| {{ p.delivery_date | formatDate }}</span>
                      </div>
                    </div>
                    <button
                     class="btn btn-link btn-sm pl-0"
                     v-if="d.proposals.length > 3 && d.lines <= 3"
                     @click="show_more(d)">{{ $t('tenderview.more') }} ...
                    </button>
                    <button
                     class="btn btn-link btn-sm pl-0"
                     v-if="d.proposals.length > 3 && d.lines > 3"
                     @click="show_less(d)">{{ $t('tenderview.less') }} ...
                    </button>
                  </td>
                  <td v-if="!is_supplier">{{ d.creator_name }}</td>
                  <td v-if="!is_supplier">
                    <div :class="statusClass(d.status)">
                      {{ $t(`details.state.${d.status}`.toLowerCase()) }}
                    </div>
                  </td>
                  <td v-if="!is_supplier">{{ d.order_number }}</td>
                  <td v-if="!is_supplier">
                    <b-c-detail-status
                      v-if="d.bc_id"
                      justify-content="left"
                      :detail="d"
                    />
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <div v-if="tender.comments_options && tender.comments_options.length > 0">
            <h4>{{ $t('tenderview.comment') }}</h4>
            <hr>
            <div
              class="comment"
              v-if="c.content"
              v-for="(c, index) in tender.comments_options"
              :key="'comment_' + index">
              <div class="table-responsive">
                <table class="table">
                  <tbody>
                  <tr>
                    <td class="b0 pl-0">
                      <p class="h4 mt-0">
                        <span class="text-bold">{{ c.supplier_name }}</span>
                      </p>
                      <div class="text-muted">{{ c.content }}</div>
                      <div>{{ c.created | formatDateTime }}</div>
                    </td>
                  </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
          <hr/>
          <comment :tender="tender" v-if="tender.approver_comment && !is_supplier" />
        </div>
      </div>
    </div>

    <modal v-if="showModal">
      <h4 class="modal-title" slot="header-text">{{ $t('modal.header.text') }}</h4>
      <div class="modal-body" slot="modal-body">
        <p class="text-bold">{{ $t('modal.section1.header') }}</p>
        <p>{{ modal_data_1 }}</p>
        <p class="text-bold" v-if="modal_data_2">{{ $t('modal.section2.header') }}</p>
        <p>{{ modal_data_2 }}</p>
        <p class="text-bold" v-if="modal_data_3.length > 0 && !is_supplier" >{{ $t('modal.section3.header') }}</p>
        <p class="text-break" v-if="!is_supplier">{{ modal_data_3 }}</p>
        <template v-if="modal_part_comment">
          <p class="text-bold">{{ $t('modalcomment.comment') }}</p>
          <p style="white-space: break-spaces">{{ modal_part_comment }}</p>
        </template>
      </div>
      <div slot="modal_buttons">
        <button class="btn btn-secondary" type="button" @click="close_modal()">{{ $t('modal.close') }}</button>
      </div>
    </modal>

    <modal v-if="showAddToBlackListModal">
      <h4 class="modal-title" slot="header-text">{{ $t('modalblacklistadd.title') }}</h4>
      <div class="modal-body" slot="modal-body">
        <div class="form-group">
          <p class="text-bold">{{ $t('modalblacklistadd.part-title') }}</p>
          <span v-if="!is_supplier">
            {{ detail.part_code }}
          </span>
          {{ detail.part_name }}
        </div>
        <div class="form-group">
          <label class="text-bold" for="blacklist_producers">{{ $t('blacklist.producer') }} *</label>
          <multiselect
            :class="{'parsley-error': errors.name}"
            :clear-on-select="false"
            :close-on-select="false"
            :hide-selected="true"
            :internal-search="false"
            :limit="25"
            :loading="isLoading"
            :max-height="800"
            :multiple="true"
            :options="detail.proposal_producers"
            :options-limit="300"
            :searchable="true"
            :show-no-results="false"
            id="blacklist_producers"
            label="name"
            open-direction="bottom"
            :placeholder="$t('blacklist.search-and-choose')"
            track-by="id"
            v-model="detail.blacklisted_producers_info">
            <template slot="tag" slot-scope="{ option, remove }">
              <span class="custom__tag">
                <span>{{ option.name }}</span>
                  <a @click="remove(option)" class="search-choice-close"></a>
              </span>
            </template>
            <template slot="noOptions">{{ $t('global.no-options') }}</template>
            <template slot="clear" slot-scope="props">
              <div
                @mousedown.prevent.stop="clearAll(props.search)"
                class="multiselect__clear"
                v-if="producers.length"
              ></div>
            </template>
            <span slot="noResult">{{ $t('blacklist.message-no-elements') }}</span>
          </multiselect>
          <ul class="parsley-errors-list filled" v-if="errors.producers">
            <li class="parsley-required">{{ errors.producers[0] }}</li>
          </ul>
        </div>
        <div class="form-group">
          <label class="text-bold" > {{ $t('modalblacklistadd.comment') }} *</label>
          <textarea
            v-model="blacklist_comment.content"
            class="form-control"
            :class="{'parsley-error': errors.blacklist_comment}"
            :placeholder="$t('tenderview.add-comment')"
            rows="2"
            type="text">
          </textarea>
          <ul class="parsley-errors-list filled" v-if="errors.blacklist_comment">
            <li class="parsley-required">{{ errors.blacklist_comment[0] }}</li>
          </ul>
        </div>
      </div>
      <div slot="modal_buttons">
        <button class="btn btn-secondary" type="button" data-dismiss="modal" @click="close_add_to_black_list_modal()">{{ $t('button.cancel') }}</button>
        <button class="btn btn-success" type="button" @click="save()">{{ $t('blacklists.add') }}</button>
      </div>
    </modal>

    <modal v-if="showAddDetailCommentModal">
      <h4 class="modal-title" slot="header-text">{{ $t('modalcomment.title') }}</h4>
      <div class="modal-body" slot="modal-body">
        <div class="form-group">
          <p class="text-bold">{{ $t('modalcomment.part-title') }}</p>
          <span v-if="!is_supplier">
            {{ detail.part_code }}
          </span>
          {{ detail.part_name }}
        </div>
        <div class="form-group">
          <label class="text-bold" > {{ $t('modalcomment.comment') }} *</label>
          <textarea
            v-model="detail_comment.content"
            class="form-control"
            :class="{'parsley-error': errors.detail_comment}"
            :placeholder="$t('tenderview.add-detail-comment')"
            rows="2"
            type="text">
          </textarea>
          <ul class="parsley-errors-list filled" v-if="errors.detail_comment">
            <li class="parsley-required">{{ errors.detail_comment[0] }}</li>
          </ul>
        </div>
      </div>
      <div slot="modal_buttons">
        <button class="btn btn-secondary" type="button" data-dismiss="modal" @click="close_add_detail_comment_modal()">{{ $t('button.cancel') }}</button>
        <button class="btn btn-success" type="button" @click="save_detail_comment()">{{ $t('modalcomment.save') }}</button>
      </div>
    </modal>

    <modal v-if="showModalDetailComments">
      <h4 class="modal-title" slot="header-text">{{ $t('detailcomments.comments-title') }}</h4>
      <div class="modal-body pre-scrollable" slot="modal-body">
        <div class="form-group">
          <label class="text-bold m-0">{{ $t('detailscomments.part-title') }}:</label>
          <p class="form-control-plaintext">
          <span v-if="!is_supplier">
            {{ row.part_code }}
          </span>
          {{ row.part_name }}
          </p>
          <h4 class="mt-2 text-bold"> {{ $t('detailcomments.comments-log') }}</h4>
        </div>
        <div
          class="form-group"
          v-for="(c, index) in row.supplier_comments"
          :key="'comment_' + index">
          <hr>
          <p class="form-control-plaintext">
             <span class="text-bold">{{c.name}}({{c.supplier}})</span>
             <span class="text-muted">{{c.created | formatDateTime}} </span>
          </p>
          <p class="form-control-plaintext">{{c.content}}</p>
        </div>
        <div class="form-group" v-if="row.winner_comment">
          <hr>
          <p class="form-control-plaintext">
            <span class="text-bold text-info">{{row.winner_comment_data.name}}</span>
            <span class="text-muted">{{row.winner_comment_data.created | formatDateTime}} </span>
          </p>
          <p class="form-control-plaintext">{{row.winner_comment_data.content}}</p>
        </div>
      </div>
      <div slot="modal_buttons">
        <button
          class="btn btn-secondary"
          type="button"
          @click="close_detail_comments_modal()">{{ $t('modal.close') }}
        </button>
      </div>
    </modal>

  </section>
</template>

<script>
import api from '@/api'
import { Datetime } from 'vue-datetime'
import message from '@/message'
import Suggest from '@/components/Suggest.vue'
import moment from 'moment'
import VueCountdown from '@chenfengyuan/vue-countdown'
import * as download from 'downloadjs'
import {parseApiError} from '@/utils'
import Modal from '@/components/Modal.vue'
import Multiselect from 'vue-multiselect'
import Comment from '@/components/Comment.vue'
import VueCurrencyInput from 'vue-currency-input'
import Gallery from "@/components/Gallery.vue"
import BCTenderStatus from "@/components/BCTenderStatus.vue";
import BCDetailStatus from "@/components/BCDetailStatus.vue";

const getData = (id, route, next) => {
  Promise.all([
    api.get(`/tenders/${id}/`),
    api.get(`/details/?tender=${id}&page_size=0`)
  ])
    .then(([tender, details]) => {
      let data = details.data

      if (tender.data.approver_comment === null) {
        tender.data.input_approver_comment = {
          content: ''
        }
      } else {
        tender.data.input_approver_comment = {
          id: tender.data.approver_comment,
          content: tender.data.approver_comment_content
        }
      }

      data.forEach(d => {
        d.errors = {}
        d.proposals_options.forEach(p => { p.errors = {} })
        d.lines = 3
        d.updateProposal = null
        d.commentInput = false
        d.commentLoading = false

        if (d.winners.length === 0) {
          d.winners = [{
            name: '',
            amount: 0,
            reason: '',
            errors: {}
          }]
        }

        d.winners_data = []

        if (d.proposals.length && d.proposals_options.length) {
          d.proposals = d.proposals_options
          if (d.winners) {
            d.proposals.forEach(p => {
              let winner = d.winners.find(winner => winner.name === p.id)
              if (winner) {
                p.errors = {}
                d.winners_data.push(p)
              }
            })
          }
        } else {
          d.proposals = [{
            producer: {
              id: null,
              name: ''
            },
            errors: {},
            detail: d,
            count: d.proposals.length
          }]
        }

        d.comment = ''

        if (d.winners_data.length === 0) {
          d.winners_data = [{
            supplier_name: null,
            price: null,
            producer: {
              name: null
            },
            product_code: null,
            delivery_days: null,
            delivery_date: null,
            available_qty: 0,
            additional_qty: 0,
            total_approved_qty: 0,
            user__email: null,
            errors: {}
          }]
        }
      })

      next({
        tender: tender.data,
        details: data,
        comment: {
          id: tender.data.comments_options.length ? tender.data.comments_options[0].id : null,
          content: tender.data.comments_options.length ? tender.data.comments_options[0].content : ''
        }
      })
    })
}

export default {
  components: {
    BCDetailStatus,
    BCTenderStatus,
    datetime: Datetime,
    suggest: Suggest,
    countdown: VueCountdown,
    modal: Modal,
    comment: Comment,
    Multiselect,
    VueCurrencyInput,
    silentBox: Gallery,
  },
  data () {
    return {
      tender: {},
      agree_error: null,
      details: [],
      producers: [],
      comment: {},
      showModal: false,
      modal_data_1: null,
      modal_data_2: null,
      modal_data_3: null,
      modal_part_comment: null,
      showAddToBlackListModal: false,
      blacklist_producers: [],
      prev_blacklist_producers: [],
      blacklist_comment: {content: ''},
      isLoading: false,
      options: [],
      errors: {},
      showAddDetailCommentModal: false,
      showModalDetailComments: false,
      loader: false,
      backendOperations: [],
      backendOperationsErrors: [],
      disableConfirmBtn: false,
      approveCommentLoading: false
    }
  },
  mounted () {
    this.$parent.menu = 'tenders'
    this.$parent.submenu = ''
    this.$i18n.locale = this.$store.getters.language
  },
  computed: {
    till_end () {
      let seconds = new Date().getTime()
      let finish = new Date(this.tender.finish).getTime()
      return finish - seconds
    },
    is_active () {
      return this.tender.status === 'ACTIVE'
    },
    is_choosing () {
      return this.tender.status === 'CHOOSING'
    },
    is_waiting () {
      return this.tender.status === 'WAITING'
    },
    is_finished () {
      return this.tender.status === 'FINISHED'
    },
    is_ordered () {
      return this.tender.status === 'ORDERED'
    },
    is_canceled () {
      return this.tender.status === 'CANCELED'
    },
    is_failed () {
      return this.tender.status === 'FAILED'
    },
    is_supplier () {
      return this.$store.getters.user_group === 'Supplier'
    },
    is_user_able_to_blacklist () {
      return this.$store.getters.user_group === 'Administrator' || this.$store.getters.user_group === 'Contractor'
    },
    total_qty () {
      let total = 0
      this.details.forEach(detail => {
        total += detail.qty
      })
      return total
    },
    total () {
      let total = 0
      this.details.forEach(detail => {
        let cheapest = this.get_cheapest_proposal(detail.proposals)
        if (cheapest) {
          total += this.sub_price(detail.qty, cheapest.price)
        }
      })
      return total.toFixed(2)
    },
    selectedWinners() {
      const selectedDetails = []
      let winnerDetails = 0
      let orderedDetails = 0
      let proposalessDetails = 0

      this.details.forEach(detail => {
        if (detail.status === "ORDERED") {
          orderedDetails++;
          return;
        }

        for (let i = 0; i < detail.winners.length; i++) {
          if (detail.winners[i].name && detail.winners[i].reason) {
            winnerDetails++;
            selectedDetails.push(detail);
            return;
          }
        }

        if (!this.has_proposals(detail)) {
          proposalessDetails++;
        }
      })

      return {
        details: selectedDetails,
        count: this.details.length === (winnerDetails + orderedDetails + proposalessDetails) ? 0 : winnerDetails
      }
    }
  },
  methods: {
    statusClass (status) {
      let bgColor = 'bg-white'
      switch (status.toUpperCase()) {
        case 'PREPARED':
          bgColor = 'bg-gray'
          break
        case 'ORDERED':
          bgColor = 'bg-info-light'
          break
        case 'WAITING':
          bgColor = 'bg-warning-light'
          break
        case 'FAILED':
          bgColor = 'bg-danger-dark'
          break
        default:
          break
      }
      return 'rounded badge px-4 ml-2 text-sm d-lg-inline ' + bgColor
    },
    supplierStatus(status, winner) {
      let bgColor;
      let text;

      if (status === "ORDERED") {
        bgColor = winner ? 'bg-success-light' : 'bg-danger-light'
        text = winner ? 'winner' : 'not-selected'
      } else {
        bgColor = 'bg-warning-light'
        text = 'choosing'
      }

      return {
        class: 'rounded badge px-4 text-sm d-lg-inline ' + bgColor,
        text: text
      }
    },
    add_agreed(id) {
      api.patch(`/tenders/${id}/add_log/`, {action: 'AGREED'})
          .then(() => {
              this.agree_error = null
              this.$set(this.tender, 'agreed', true)
          })
    },
    remove_agreed(id) {
       api.patch(`/tenders/${id}/remove_log/`, {action: 'AGREED'})
         .then(() => {
             this.$set(this.tender, 'agreed', false)
         })
    },
    add_watched(id) {
      this.$swal({
        type: 'info',
        title: this.$i18n.t('tenderview.are-you-sure'),
        text: this.$i18n.t('tenderview.are-you-sure-add-watched'),
        showCancelButton: true
      })
        .then((choice) => {
          if (choice.value) {
            api.patch(`/tenders/${id}/add_log/`, {action: 'WATCHED'})
              .then(() => {
                this.$set(this.tender, 'watched', true)
                message.success(this.$i18n.t('tenderview.tender-watched'))
                })
            }
          })
    },
    remove_watched(id) {
      this.$swal({
        type: 'warning',
        title: this.$i18n.t('tenderview.are-you-sure'),
        text: this.$i18n.t('tenderview.are-you-sure-remove-watched'),
        showCancelButton: true
      })
        .then((choice) => {
          if (choice.value) {
            api.patch(`/tenders/${id}/remove_log/`, {action: 'WATCHED'})
              .then(() => {
                this.$set(this.tender, 'watched', false)
                message.success(this.$i18n.t('tenderview.tender-unwatched'))
              })
            }
          })
    },
    close_modal () {
      if (this.showModal) {
        this.showModal = false
      } else if (this.showAddToBlackListModal) {
        this.showAddToBlackListModal = false
      } else if (this.showAddDetailCommentModal) {
        this.showAddDetailCommentModal = false
      } else if (this.showModalDetailComments) {
        this.showModalDetailComments = false
      }
    },
    get_cheapest_proposal (proposals) {
      let cheapest = null
      proposals.forEach(proposal => {
        if ((this.get_proposal_data(proposal) != null) && (cheapest === null || Number(proposal.price) < Number(cheapest.price))) {
          cheapest = proposal
        }
      })
      return cheapest
    },
    open_modal (data1, data2, data3, part_comment) {
      this.showModal = true
      this.modal_data_1 = data1
      this.modal_data_2 = data2
      this.modal_data_3 = data3.map(i => i.name).join('; ')
      this.modal_part_comment = part_comment
    },
    close_add_to_black_list_modal () {
      this.showAddToBlackListModal = false
    },
    open_add_to_black_list_modal (detail) {
      this.showAddToBlackListModal = true
      this.detail = detail
      this.prev_blacklist_producers = this.detail.blacklisted_producers_info
      this.errors['blacklist_comment'] = null
      this.errors['producers'] = null
    },
    open_add_detail_comment_modal (detail) {
      this.showAddDetailCommentModal = true
      this.detail = detail
      if (detail.supplier_comments.length === 0) {
        this.detail_comment = {content: '', id: null}
      } else {
        this.detail_comment = Object.assign({}, detail.supplier_comments[0])
      }
      this.errors['detail_comment'] = null
    },
    close_add_detail_comment_modal () {
      this.showAddDetailCommentModal = false
    },
    open_detail_comments_modal (detail) {
      this.row = detail
      this.showModalDetailComments = true
    },
    close_detail_comments_modal () {
      this.showModalDetailComments = false
    },
    exportExcel (id) {
      api.get(`/tenders/${id}/export_excel/`, {responseType: 'blob'})
        .then(resp => {
          download(
            resp.data,
            'tender_' + this.tender.id + '_' + new Date().toLocaleDateString() + '.xls',
            resp.headers['content-type']
          )
        })
    },
    exportPDF (id) {
      api.get(`/tenders/${id}/export_pdf/`, {responseType: 'blob'})
        .then(resp => {
          download(
            resp.data,
            'tender_' + this.tender.id + '_' + new Date().toLocaleDateString() + '.pdf',
            resp.headers['content-type']
          )
        })
    },
    start_choosing () {
      this.tender.status = 'CHOOSING'
      api.patch(`/tenders/${this.tender.id}/`, {
        ...this.tender
      })
      setTimeout(() => {
        message.success(this.$i18n.t('tenderview.choosing'))
        this.$router.push({name: 'tender_view', params: {id: this.tender.id}})
      }, 1000)
    },
    cancel_proposals () {
      this.$swal({
        type: 'warning',
        title: this.$i18n.t('tenderview.are-you-sure'),
        text: this.$i18n.t('tenderview.are-you-sure-text'),
        showCancelButton: true
      })
        .then((choice) => {
          if (choice.value) {
            this.details.forEach(d => {
              this.delete_all_additional_proposals(d)
            })

            if (this.comment.id) {
              api.delete(`/comments/${this.comment.id}/`)
              this.comment.id = null
            }
            this.comment.content = null

            setTimeout(() => {
              message.success('Pakkumine tühistatud!')
            }, 1000)
          }
        })
    },
    cancel_tender () {
      this.$swal({
        type: 'warning',
        title: this.$i18n.t('tenderview.are-you-sure'),
        text: this.$i18n.t('tenderview.are-you-sure-text-cancel'),
        showCancelButton: true
      })
        .then((choice) => {
          if (choice.value) {
            this.tender.status = 'CANCELED'
            api.patch(`/tenders/${this.tender.id}/`, {
              ...this.tender
            })
            this.details.forEach(detail => {
              let proposals = []
              detail.proposals.forEach(p => {
                if (p.id) {
                  proposals.push(p.id)
                }
              })
              api.patch(`/details/${detail.id}/`, {
                ...detail,
                status: 'FAILED',
                proposals: proposals
              })
            })
            setTimeout(() => {
              message.success('Ostukonkurss tühistatud!')
              this.$router.push({name: 'tenders'})
            }, 1000)
          }
        })
    },
    is_mandatory_fields_set (proposal) {
      if (proposal.producer != null &&
        proposal.producer.name !== '' &&
        proposal.delivery_days != null &&
        proposal.product_code != null &&
        proposal.price != null &&
        proposal.price !== '') {
        return true
      }
      return false
    },
    delete_all_additional_proposals (detail) {
      detail.proposals.forEach(p => {
        if (p.id) {
          api.delete(`/proposals/${p.id}/`)
        }
        p.id = null
        p.producer = {id: null, name: ''}
        p.delivery_days = null
        p.product_code = null
        p.price = null
        p.additional_qty = null
        p.available_qty = null
      })

      api.patch(`/details/${detail.id}/delete_supplier_comment/`, {})
      detail.supplier_comments = []

      while (detail.proposals.length >= 2) {
        this.$delete(detail.proposals, 1)
      }
    },
    get_winner (d, p) {
      return d.winners.find(winner => winner.name === p.id)
    },
    delete_additional_proposal (proposal, proposals) {
      let id = proposal.id
      let index = proposals.indexOf(proposal)
      this.$delete(proposals, index)
      if (id !== null) {
        api.delete(`/proposals/${id}/`)
      }
    },
    winner_proposal_selected (detail, event) {
      if (event && event.target.value !== '') {
        let minPrice = 0
        detail.proposals.forEach(proposal => {
          if (minPrice === 0 || proposal.price < minPrice) {
            minPrice = proposal.price
          }
        })
        let detailProposal = detail.proposals.find(proposal => proposal.id === parseInt(event.target.value))
        let winner = detail.winners.find(winner => winner.name === parseInt(event.target.value))
        winner.reason = ''

        if (detailProposal.price === minPrice) {
          winner.reason = 'PRICE'
        }

        if (this.validate_winners()) {
          winner.amount = detail.qty
        }
      } else {
        this.validate_winners()
      }
    },
    winner_reason_selected (winner, event) {
      if (event && this.validate_winners() && event.target.value !== '') {
        winner.reason = event.target.value
      }
    },
    validate_winners () {
      let isValid = true
      this.details.forEach(detail => {
        let sum = 0
        let supplierProposals = []

        detail.winners.forEach(winner => {
          winner.errors = {}

          // Empty option selected, let's purge inputs
          if (winner.name === '') {
            winner.amount = 0
            winner.reason = ''
          }
          let dProposal = detail.proposals.find(proposal => proposal.id === winner.name)

          if (!dProposal) {
            return
          }

          if (detail.winners.length === 1) {
            winner.amount = detail.qty
          }

          if (parseInt(detail.qty) > 0) {
            sum += parseInt(detail.qty)
          } else {
            winner.errors['amount'] = [this.$i18n.t('tenderview.total_amount-is-zero')]
            isValid = false
          }

          if (winner.reason === '') {
            winner.errors['reason'] = [this.$i18n.t('tenderview.please-choose-winner-reason')]
            isValid = false
          }

          if (supplierProposals.find(sProposal => sProposal === dProposal.id)) {
            winner.errors['supplier'] = [this.$i18n.t('tenderview.dublicate-supplier-proposal')]
            isValid = false
          } else {
            supplierProposals.push(dProposal.id)
          }
        })

        if (sum > detail.qty) {
          detail.winners.forEach(winner => {
            winner.errors['amount'] = [this.$i18n.t('tenderview.total_amount-above-need')]
            isValid = false
          })
        }
      })
      return isValid
    },
    confirm_tender (winners, tender) {
      let tenderNewData = {}
      tenderNewData = JSON.parse(JSON.stringify(tender))
      tenderNewData.status = (winners > 0) ? 'ORDERED' : 'FAILED'
      api.patch(`/tenders/${this.tender.id}/`, {
        ...tenderNewData})
        .then((resp) => {
          this.tender = resp.data
          this.details.forEach(detail => {
            detail.winners_data = []
            detail.proposals.forEach(p => {
              let winner = detail.winners.find(winner => winner.name === p.id)
              if (winner) {
                detail.winners_data.push(p)
              }
            })
          })
          this.loader = false
          message.success((winners > 0) ? this.$i18n.t('tenderview.winners') : this.$i18n.t('tenderview.no-winners'))
        })
        .catch(error => {
          this.loader = false
          message.error(parseApiError(error, this.$i18n))
        })
    },
    handle_operation_error (error) {
      this.loader = false
      this.backendOperationsErrors.push(error)
      message.error(parseApiError(error, this.$i18n))
    },
    submitSelectedWinners() {
      this.$swal({
        type: 'warning',
        title: this.$i18n.t('modal-messages.submit-order'),
        showCancelButton: true
      })
        .then((choice) => {
          if (choice.value) {
            this.disableConfirmBtn = true
            const updatedDetailIds = []
            const updateRequests = []

            this.selectedWinners.details.forEach(detail => {
              detail.status = "ORDERED"

              let proposals = []

              detail.proposals.forEach(p => {
                if (p.id) {
                  proposals.push(p.id)
                }
              })

              updateRequests.push(
                api.patch(`/details/${detail.id}/add_winners/`, {winners: detail.winners})
                  .then(() => {
                    return api.patch(`/details/${detail.id}/`, {
                      ...detail,
                      status: 'ORDERED',
                      proposals: proposals
                    })
                      .then(json => {
                        detail.status = json.data.status
                        detail.purchaser_id = json.data.purchaser_id
                        detail.bc_sync_running = json.data.bc_sync_running
                        detail.proposals.forEach(proposal => {
                          proposal.delivery_date = json.data.proposals_options.find(opt => opt.id === proposal.id).delivery_date
                        })
                        updatedDetailIds.push(json.data.id)
                      })
                      .catch(() => {
                        detail.status = "WAITING"
                      })
                  })
                  .catch(() => {
                    detail.status = "WAITING"
                  })
              )
            })

            Promise.all(updateRequests)
              .then(() => {
                if (updatedDetailIds.length) {
                  api.post(
                    `/tenders/${this.tender.id}/notify-winners/`,
                    { detail_set: updatedDetailIds }
                  )
                    .then(() => {
                      message.success(this.$i18n.t('detail.updated'))
                      this.disableConfirmBtn = false
                  })
                    .catch(error => {
                      message.error(parseApiError(error, this.$i18n))
                      this.disableConfirmBtn = false
                    })
                } else {
                  this.disableConfirmBtn = false
                  message.error(this.$i18n.t('message-errors.error'))
                }
            })
          }
        })
    },
    submitWinners() {
      if (!this.validate_winners()) return;

      if (this.selectedWinners.count === 0) {
        this.$swal({
          type: 'warning',
          title: this.$i18n.t('modal-messages.submit-tender'),
          showCancelButton: true
        })
          .then((choice) => {
            if (choice.value) {
              this.confirm_winners()
            }
          })
      } else {
        this.submitSelectedWinners()
      }
    },
    toggleWinnerComment(detail) {
      detail.commentInput = !detail.commentInput
      detail.comment = ''
      detail.errors['winner_comment'] = null
    },
    saveWinnerComment(detail, btnClick = false) {
      if (this.validateWinnerComment(detail)) return;
      detail.errors['winner_comment'] = null

      if (btnClick) {
        detail.commentLoading = true
      }

      if (detail.comment) {
        api.post(`/comments/`, {content: detail.comment})
          .then(resp => {
            detail.winner_comment = resp.data.id
            detail.winner_comment_data = {
              id: resp.data.id,
              name: resp.data.creator,
              created: resp.data.created,
              content: resp.data.content
            }

            if (btnClick) {
              api.patch(`/details/${detail.id}/`, {
                part: detail.part,
                replacement_qty: detail.replacement_qty,
                replenishment_qty: detail.replenishment_qty,
                winner_comment: resp.data.id
              })
                .then(resp => {
                    message.success(this.$i18n.t('add_comment.supplier-comment-added'))
                    this.toggleWinnerComment(detail)
                })
                .catch(error => {
                  message.error(this.$i18n.t('message-errors.error'))
                })
                .finally(() => {
                  detail.commentLoading = false
                })
            }
          })
          .catch(error => {
            message.error(this.$i18n.t('message-errors.error'))
            detail.commentLoading = false
          })
      }
    },
    validateWinnerComment(detail) {
      if (!detail.comment) {
        detail.errors['winner_comment'] = [this.$i18n.t('tenderview.error-winner-comment')]
        this.$forceUpdate()
        return true
      }
      if (!detail.comment.trim().length) {
        detail.errors['winner_comment'] = [this.$i18n.t('tenderview.comment-cant-contain-just-spaces')]
        this.$forceUpdate()
        return true
      }
      return false
    },
    confirm_winners () {
      if (!this.validate_winners()) return;

      let hasError = false
      this.details.forEach(detail => {
        detail.errors['winner_comment'] = null
        if (detail.winners.length > 1) {
          hasError = this.validateWinnerComment(detail)
        }
      })

      if (hasError) {
        return
      }

      this.loader = true
      this.backendOperations = []
      this.backendOperationsErrors = []
      let winners = 0
      let updatedDetailIds = []

      this.details.forEach(detail => {
        if (detail.status === "ORDERED") {
          winners++
          return
        }

        let proposals = []
        let minPrice = 0

        detail.proposals.forEach(p => {
          if (p.id) {
            proposals.push(p.id)
          }
          if (minPrice === 0 || p.price < minPrice) {
            minPrice = p.price
          }
        })

        detail.winners.forEach(w => {
          let dProposal = detail.proposals.find(proposal => proposal.id === w.name)
          if (dProposal) {
            if (w.reason === '' && dProposal.price === minPrice) {
              w.reason = 'PRICE'
            }
          }
        })

        this.saveWinnerComment(detail)

        if (detail.winners && detail.winners.filter(w => w.name !== '').length > 0) {
          winners++
          updatedDetailIds.push(detail.id)

          this.backendOperations.push(api.patch(`/details/${detail.id}/add_winners/`, {winners: detail.winners})
            .then(() => {
              return api.patch(`/details/${detail.id}/`, {
                ...detail,
                status: 'ORDERED',
                proposals: proposals
              })
                .then(json => {
                  detail.status = json.data.status
                  detail.bc_sync_running = json.data.bc_sync_running
                  detail.proposals.forEach(proposal => {
                    proposal.delivery_date = json.data.proposals_options.find(opt => opt.id === proposal.id).delivery_date
                  })
                })
                .catch(error => {
                  this.handle_operation_error(error)
                })
            })
            .catch(error => {
              this.handle_operation_error(error)
            }))
        } else {
          this.backendOperations.push(api.patch(`/details/${detail.id}/delete_winners/`, {})
            .catch(error => {
              this.handle_operation_error(error)
            }).then(() => {
              return api.patch(`/details/${detail.id}/`, {
                ...detail,
                status: 'FAILED',
                proposals: proposals
              }).then(json => {
                  detail.status = json.data.status
                  detail.bc_sync_running = json.data.bc_sync_running
              })
                .catch(error => {
                  this.handle_operation_error(error)
                })
            }))
        }
      })

      Promise.all(this.backendOperations).then(() => {
        if (this.backendOperationsErrors.length > 0) {
          return
        }

        if (updatedDetailIds.length) {
          api.post(
            `/tenders/${this.tender.id}/notify-winners/`,
            {detail_set: updatedDetailIds}
          )
            .then(() => {
              this.disableConfirmBtn = false
            })
            .catch(error => {
              message.error(parseApiError(error, this.$i18n))
              this.disableConfirmBtn = false
            })
        }

        let data = this.tender
        if (data.input_approver_comment.content) {
          if (data.input_approver_comment.id) {
            api.patch(`/comments/${data.input_approver_comment.id}/`, data.input_approver_comment)
              .then(() => {
                this.confirm_tender(winners, data)
              })
              .catch(error => {
                this.loader = false
                message.error(parseApiError(error, this.$i18n))
              })
          } else {
            api.post(`/comments/`, data.input_approver_comment)
              .then(resp => {
                data.approver_comment = resp.data.id
                data.input_approver_comment.id = resp.data.id
                this.confirm_tender(winners, data)
              })
              .catch(error => {
                this.loader = false
                message.error(parseApiError(error, this.$i18n))
              })
          }
        } else {
          this.confirm_tender(winners, data)
        }
      })
        .catch(error => {
          this.loader = false
          message.error(parseApiError(error, this.$i18n))
        })
    },
    update_approver_comment() {
      if (!this.tender.input_approver_comment.content.trim().length) return;

      this.approveCommentLoading = true

      if (this.tender.input_approver_comment.id) {
        api.patch(`/comments/${this.tender.input_approver_comment.id}/`, this.tender.input_approver_comment)
          .then(() => {
            message.success(this.$i18n.t('add_comment.supplier-comment-added'))
          })
          .catch(error => {
            message.error(parseApiError(error, this.$i18n))
          })
          .finally(() => {
            this.approveCommentLoading = false
          })
      } else {
        api.post(`/comments/`, this.tender.input_approver_comment)
          .then(resp => {
            this.tender.approver_comment = resp.data.id
            this.tender.input_approver_comment.id = resp.data.id

            return api.patch(`/tenders/${this.tender.id}/approver-comment/`, { approver_comment: resp.data.id })
              .then(() => {
                message.success(this.$i18n.t('add_comment.supplier-comment-added'))
              })
              .catch(error => {
                message.error(this.$i18n.t('message-errors.error'))
              })
          })
          .catch(error => {
            message.error(parseApiError(error, this.$i18n))
          })
          .finally(() => {
            this.approveCommentLoading = false
          })
      }
    },
    has_proposals (detail) {
      for (let i = 0; i < detail.proposals.length; i++) {
        if (detail.proposals[i].supplier_id && detail.proposals[i].supplier_id > 0) {
          return true
        }
      }
      return false
    },
    show_more (d) {
      d.lines = d.proposals.length
    },
    show_less (d) {
      d.lines = 3
    },
    number_of_lines (data, num) {
      let lines = []
      for (let i = 0; i < num; i++) {
        lines.push(data[i])
      }
      return lines
    },
    validate_proposal (proposal, p) {
      let hasError = false

      if (!proposal.producer) {
        hasError = true
        p.errors['producer'] = [this.$i18n.t('tenderview.producer-should-exist')]
      }

      if (proposal.price == null) {
        hasError = true
        p.errors['price'] = [this.$i18n.t('tenderview.price-can-not-be-empty')]
      }

      if (isNaN(proposal.price)) {
        hasError = true
        p.errors['price'] = [this.$i18n.t('tenderview.incorrect-price')]
      }

      if (!isNaN(proposal.price) && proposal.price < 0.0001) {
        hasError = true
        p.errors['price'] = [this.$i18n.t('tenderview.price-more-than-zero')]
      }

      if (!proposal.delivery_days) {
        hasError = true
        p.errors['delivery_days'] = [this.$i18n.t('tenderview.delivery-date-should-exist')]
      }

      const maxDelivery = this.tender.max_delivery || 32767
      if (Number(proposal.delivery_days) > maxDelivery) {
        hasError = true
        p.errors['delivery_days'] = [`${this.$i18n.t('Maximum')}: ${maxDelivery}`]
      }

      if (!proposal.product_code || !proposal.product_code.trim()) {
        hasError = true
        p.errors['product_code'] = [this.$i18n.t('tenderview.product-code-should-exist')]
      }

      return hasError
    },
    get_proposal_data (data) {
      let proposal = {
        price: data.price ? Number(data.price.toString().replace(',', '.').replace(' ', '')) : null,
        additional_qty: data.additional_qty ? data.additional_qty : null,
        available_qty: data.available_qty ? data.available_qty : null,
        product_code: data.product_code || null
      }

      if (proposal.price) {
        return proposal
      }

      return null
    },
    assemblyProposalFromData (data) {
      let proposal = {
        id: data.id ? data.id : null,
        producer: data.producer.id ? data.producer.id : null,
        delivery_days: data.delivery_days ? data.delivery_days : null,
        price: data.price ? Number(data.price.toString().replace(',', '.').replace(' ', '')) : null,
        additional_qty: data.additional_qty ? data.additional_qty : null,
        available_qty: data.available_qty ? data.available_qty : null,
        product_code: data.product_code || null
      }

      if (proposal.producer || proposal.delivery_days || proposal.price || proposal.product_code) {
        return proposal
      }

      return null
    },
    add_proposal () {
      let hasError = false

      if (!this.tender.agreed) {
        hasError = true
        this.agree_error = this.$i18n.t('tenderview.agree-error')
      }

      if (this.comment.content) {
        if (this.comment.id) {
          api.patch(`/comments/${this.comment.id}/`, this.comment)
        } else {
          api.patch(`/tenders/${this.tender.id}/add_comment/`, this.comment)
            .then(resp => {
              this.comment.id = resp.data.id
            })
        }
      } else {
        if (this.comment.id) {
          api.delete(`/comments/${this.comment.id}/`)
          this.comment.id = null
        }
      }
      let numLines = 0
      this.details.forEach(d => {
        d.proposals.forEach(p => {
          p.errors = {}

          if (d.blacklisted_producers.includes(p.producer.id)) {
            hasError = true
            p.errors['producer'] = [this.$i18n.t('tenderview.blacklisted-producer')]
          } else {
            let proposal = this.assemblyProposalFromData(p)
            if (proposal != null) {
              p.req_qty = d.qty

              if (this.validate_proposal(proposal, p) && hasError === false) {
                hasError = true
              }

              if (hasError === false) {
                numLines += 1
                if (proposal.id) {
                  api.patch(`/proposals/${proposal.id}/`, proposal)
                    .catch(error => {
                      hasError = true
                      if (error.response && error.response.status === 400) {
                        p.errors = parseApiError(error, this.$i18n)
                      }
                    })
                } else {
                  api.patch(`/details/${d.id}/add_proposal/`, proposal)
                    .then(resp => {
                      p.id = resp.data.id
                    })
                }
              }
            }
          }
        })
      })
      if (numLines === 0 && !hasError) {
        hasError = true
        message.error(this.$i18n.t('tenderview.minimum-one-part'))
      }
      if (hasError === false && numLines > 0) {
        setTimeout(() => {
          message.success(this.$i18n.t('tenderview.proposal-success'))
          this.$router.push({name: 'tenders'})
        }, 1000)
      }
    },
    append_additional_proposal (detail) {
      let p = {
        id: null,
        detail: detail,
        producer: {
          id: null,
          name: ''
        },
        delivery_days: null,
        price: null,
        additional_qty: null,
        available_qty: null,
        product_code: null,
        errors: {}
      }
      detail.proposals.push(p)
    },
    sub_total (qty, price) {
      let total = this.sub_price(qty, price)
      return total.toFixed(2)
    },
    sub_price (qty, price) {
      let subPrice = 0
      let curPrice = 0
      if (price) {
        curPrice = Number(price.toString().replace(',', '.').replace(' ', ''))
      }
      if (curPrice > 0) {
        subPrice = curPrice
      }
      return Number(qty || 0) * subPrice
    },
    clear_producer_options() {
      this.producers = []
    },
    search_producers (q) {
      api.get(`/producers/?is_active=true&page_size=5`, {
        params: {
          name__icontains: q
        }
      })
        .then(resp => {
          this.producers = resp.data.results.map(obj => {
            return {
              id: obj.id,
              title: obj.name
            }
          })
          if (q.length > 1 && !this.producers.find(e => e.title.toUpperCase() === q.toUpperCase())) {
            this.producers.push({
              id: 0,
              title: `Lisa tootja "${q}"`,
              name: q
            })
          }
        })
    },
    updateProposal(proposal, detail) {
      if (proposal.errors.producer) return;

      api.patch(`/proposals/${proposal.id}/`, { producer: proposal.newProducer.id })
        .then(json => {
          this.$set(proposal, 'producer', {
            id: json.data.producer.id,
            name: json.data.producer.name
          })
          message.success(this.$i18n.t('message-success.producer-edited'))
          detail.updateProposal = null
        })
        .catch(error => {
          console.log(error)
          message.error(this.$i18n.t('message-errors.producer-error'))
      })
    },
    add_producer (additionalProposal, id, name, producerKey = 'producer') {
      this.$set(additionalProposal, producerKey, {
        id: id,
        name: name
      })
      this.producers = [{
        id: id,
        title: name,
        name: name
      }]
    },
    clear_producer (obj) {
      if (obj.index.errors) {
        obj.index.errors['producer'] = null
      }
      this.$set(obj.index, 'producer', {id: null, name: ''})
      this.producers.splice(-1, 1)
    },
    select_admin_producer(option, detail) {
      option.index.detail = detail
      this.select_producer(option, 'newProducer')
    },
    select_producer (option, producerKey = 'producer') {
      if (option.index.errors) {
        option.index.errors['producer'] = null
      }
      if (option.item.id) {
        this.add_producer(option.index, option.item.id, option.item.title, producerKey)
        let detailToCheck = option.index.detail

        if (detailToCheck != null && detailToCheck.blacklisted_producers.includes(option.item.id)) {
          option.index.errors['producer'] = [this.$i18n.t('tenderview.blacklisted-producer')]
        }
      } else {
        this.$swal({
          type: 'warning',
          title: this.$i18n.t('modal-messages.add-producer'),
          showCancelButton: true
        })
        .then((choice) => {
          if (choice.value) {
            api.post(`/producers/`, {
              name: option.item.name,
              is_active: true,
              is_deleted: false
            })
              .then(resp => {
                message.success(this.$i18n.t('message-success.producer-added'))
                this.add_producer(option.index, resp.data.id, resp.data.name, producerKey)
              })
              .catch(error => {
                if (error.response && error.response.status === 400) {
                  const {name} = parseApiError(error, this.$i18n)

                  if (name.length) {
                    message.error(name[0])
                    return
                  }
                }

                message.error(this.$i18n.t('message-errors.producer-error'))
              })
          }
        })
      }
    },
    format_date_time(dt) {
      return moment(String(dt)).format('DD.MM.YYYY HH:mm')
    },
    finishTender () {
      message.success(this.$i18n.t('tenderview.tender-finished'))
      this.$router.push({name: 'tenders'})
    },
    clearAll () {
      this.blacklist_producers = []
    },
    save () {
      this.errors = {}

      let producers = []
      for (var num in this.detail.blacklisted_producers_info) {
        producers.push(this.detail.blacklisted_producers_info[num].id)
      }

      let prevProducers = []
      for (var numPrev in this.prev_blacklist_producers) {
        prevProducers.push(this.prev_blacklist_producers[numPrev].id)
      }

      let difference = prevProducers.filter(x => !producers.includes(x))
        .concat(producers.filter(x => !prevProducers.includes(x)))

      if (difference.length === 0) {
        this.errors['producers'] = [this.$i18n.t('modalblacklistadd.here-is-no-actual-change-in-producers-list')]
        return
      }

      if (this.blacklist_comment.content.trim().length === 0) {
        this.errors['blacklist_comment'] = [this.$i18n.t('modalblacklistadd.comment-cant-contain-just-spaces')]
        return
      }

      let data = {
        id: this.detail.part_blacklist,
        part: this.detail.part,
        producers: producers,
        is_active: true,
        comment: this.blacklist_comment.content
      }
      if (data.id === null) {
        api.post('/black_lists/', data)
          .then(resp => {
            this.detail.part_blacklist = resp.data.id
            message.success(this.$i18n.t('modalblacklistadd.added'))
            this.close_add_to_black_list_modal()
          })
          .catch(error => {
            let errors = parseApiError(error, this.$i18n)
            if (error.response && error.response.status === 400) {
              this.errors = parseApiError(error, this.$i18n)
            } else {
              message.error(errors)
            }
          })
      } else {
        api.patch(`/black_lists/${this.detail.part_blacklist}/`, data)
          .then(() => {
            this.close_add_to_black_list_modal()
            message.success(this.$i18n.t('modalblacklistadd.changed'))
          })
          .catch(error => {
            let errors = parseApiError(error, this.$i18n)
            if (error.response && error.response.status === 400) {
              this.errors = errors
            } else {
              message.error(errors)
            }
          })
      }
    },
    save_detail_comment () {
      this.errors = {}

      if (this.detail_comment.content.length === 0 && this.detail_comment.id != null) {
        api.delete(`/comments/${this.detail_comment.id}/`)
        this.detail.supplier_comments = []
        this.close_add_detail_comment_modal()
        message.success(this.$i18n.t('add_comment.supplier-comment-removed'))
        return
      }

      if (this.detail_comment.content.trim().length === 0) {
        this.errors['detail_comment'] = [this.$i18n.t('add_comment.comment-cant-contain-just-spaces')]
        return
      }

      if (this.detail_comment.content.length > 1000) {
        this.errors['detail_comment'] = [this.$i18n.t('add_comment.comment-cant-be-longer-1000-chars')]
        return
      }

      if (this.detail_comment.id === null) {
        api.patch(`/details/${this.detail.id}/add_supplier_comment/`, {comment: this.detail_comment.content})
          .then(resp => {
            this.detail.supplier_comments.push({id: resp.data.id, content: this.detail_comment.content})
            this.close_add_detail_comment_modal()
            message.success(this.$i18n.t('add_comment.supplier-comment-added'))
          })
      } else {
        api.patch(`/comments/${this.detail_comment.id}/`, this.detail_comment)
          .then(() => {
            this.close_add_detail_comment_modal()
            this.detail.supplier_comments[0].content = this.detail_comment.content
            message.success(this.$i18n.t('add_comment.supplier-comment-updated'))
          })
      }
    }
  },
  beforeRouteEnter (to, from, next) {
    getData(to.params.id, to, data => {
      next(vm => {
        Object.assign(vm.$data, data)
      })
    })
  },
  beforeRouteUpdate (to, from, next) {
    getData(to.params.id, to, data => {
      Object.assign(this.$data, data)
      next()
    })
  }
}
</script>



// WEBPACK FOOTER //
// src/views/TenderView.vue